// threejsBand.js - с фокусировкой камеры, дорожками и подсветкой ладов
import * as THREE from 'three';
import * as Tone from 'tone';
import {getGamingMode, getSpeedFactor} from "./game";
import {calculateDurationMs} from "./blockPlayer";
import { TIME_CONSTANTS } from './utils/timeUtils.js';
import { calculateInitialZPosition, normalizeTravelTime, NOTE_POSITION_CONSTANTS } from './utils/notePositionUtils.js';

class ThreeJSBand {
    constructor() {
        this.scene = null;
        this.camera = null;
        this.renderer = null;
        this.notes = [];
        this.streak = 0;
        this.totalNotes = 0;
        this.hitNotes = 0;
        this.fretCount = 24;

        // Правильные пропорции грифа
        this.fretWidth = 69.6;
        this.fretHeight = 5.73;

        // Позиции ладов
        this.fretPositions = this.calculateFretPositions();
        this.fretStringPositions = this.calculateFretStringPositions();

        this.container = null;
        this.fretboardGroup = null;

        this.notes = [];
        this.nextNotes = [];
        this.fallingNotes = [];
        this.appearingNotes = [];
        this.fretTracks = [];
        this.fretHighlights = []; // Для подсветки активных ладов
        this.fingerSpheres = []; // Сферы пальцев на грифе

        // Система движения нот по оси Z
        this.noteTracks = [];
        this.movingNotes = [];
        this.trackStartZ = -50;
        this.trackEndZ = 0;
        this.targetLineZ = 0;

        // Для управления камерой и отслеживания активных нот
        this.activeFrets = new Set();
        this.cameraAnimation = null;
        this.originalCameraPosition = null;
        this.fretNoteCounts = new Array(this.fretCount).fill(0); // Счетчик нот на каждом ладу

        this.lastCameraPosition = { x: 0, z: 25 }; // Сохраняем последнюю позицию
        this.defaultCameraSet = false;
        
        // Определяем мобильное устройство для оптимизации камеры
        this.isMobileDevice = this.detectMobileDevice();

        // Новая система позиционирования нот
        this.unitsPerSecond = 10; // Единиц в секунду для движения нот
        this.noteSpacingFactor = 2; // Фактор расстояния между нотами
        this.staticNoteSpacing = 8; // Расстояние между нотами в статическом режиме

        // Система приоритетов для подсветки
        this.maxVisibleTracks = 4;
        this.highlightedFret = null;

        // Оптимизация рендеринга
        this.lastHighlightUpdate = 0;
        this.lastTrackUpdate = 0;
        this.highlightUpdateInterval = 100; // Обновляем подсветку каждые 100ms
        this.trackUpdateInterval = 50; // Обновляем дорожки каждые 50ms
        this.lastSortTime = 0;
        this.sortInterval = 200; // Сортируем ноты каждые 200ms
        this.dirtyFlags = {
            highlights: true,
            tracks: true,
            camera: false
        };
        this.frameCount = 0;
        this.lastFrameTime = performance.now();
        this.noteSequenceCounter = 0;

        // Адаптивная оптимизация производительности
        this.performanceMode = 'auto'; // 'auto', 'high', 'low'
        this.fps = 60;
        this.fpsHistory = [];
        this.lastFPSCheck = performance.now();
        this.fpsCheckInterval = 2000; // Проверяем FPS каждые 2 секунды
        this.lowFPSCounter = 0;
        this.qualityLevel = 'high'; // 'high', 'medium', 'low'
        
        // Пулы для переиспользования ТОЛЬКО геометрий
        // Материалы НЕ переиспользуются - это вызывает моргание
        // Будут инициализированы в init() после определения качества
        this.geometryPool = {};

        if (document.readyState === 'loading') {
            document.addEventListener('DOMContentLoaded', () => {
                setTimeout(() => this.init(), 100);
            });
        } else {
            setTimeout(() => this.init(), 100);
        }

        this.animate();
    }

    // Детекция слабого железа
    detectMobileDevice() {
        // Определяем мобильное устройство по размеру экрана и user agent
        const isMobileScreen = window.innerWidth <= 768;
        const isMobileUA = /Android|iPhone|iPad|iPod|webOS|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        return isMobileScreen || isMobileUA;
    }

    detectLowEndDevice() {
        // Проверяем количество логических процессоров
        const cores = navigator.hardwareConcurrency || 2;
        
        // Проверяем доступную память (если API доступен)
        const memory = navigator.deviceMemory || 4; // GB
        
        // Считаем устройство слабым если:
        // - Меньше 4 ядер процессора
        // - Меньше 4 ГБ RAM
        // - Pixel ratio меньше 2 (может указывать на старый дисплей/устройство)
        const isLowEnd = cores < 4 || memory < 4 || window.devicePixelRatio < 1.5;
        
        return isLowEnd;
    }
    
    // Инициализация пулов геометрий с адаптивным качеством
    initGeometryPools(isLowEnd) {
        // На слабых ПК используем меньше полигонов
        const sphereSegments = isLowEnd ? 12 : 16;
        const coneSegments = isLowEnd ? 6 : 8;
        const ringSegments = isLowEnd ? 24 : 32;
        
        this.geometryPool = {
            sphere: new THREE.SphereGeometry(0.4, sphereSegments, sphereSegments),
            cone: new THREE.ConeGeometry(0.15, 0.4, coneSegments),
            ring: new THREE.RingGeometry(0.5, 0.7, ringSegments)
        };
        
        // Маркируем геометрии из пула, чтобы не удалять их
        Object.values(this.geometryPool).forEach(geom => {
            geom.userData.fromPool = true;
        });
    }
    
    // Безопасная очистка меша (не удаляет геометрии из пула, но удаляет все материалы)
    safeDisposeMesh(mesh) {
        if (!mesh) return;
        
        mesh.children.forEach(child => {
            // Проверяем, что геометрия не из пула
            if (child.geometry && !child.geometry.userData.fromPool) {
                child.geometry.dispose();
            }
            
            if (child.material) {
                // Удаляем текстуры (они unique для каждого меша)
                if (child.material.map) {
                    child.material.map.dispose();
                }
                
                // Удаляем ВСЕ материалы (они больше не переиспользуются)
                child.material.dispose();
            }
            
            // Рекурсивно очищаем детей
            if (child.children && child.children.length > 0) {
                this.safeDisposeMesh(child);
            }
        });
    }

    // Инициализация системы треков
    initNoteTracks() {
        this.noteTracks = [];

        for (let fret = 0; fret < this.fretCount; fret++) {
            const track = {
                fret: fret,
                startZ: this.trackStartZ,
                endZ: this.trackEndZ,
                width: 0,
                color: this.getTrackColor(fret),
                visible: false // По умолчанию скрыты
            };

            if (this.fretPositions[fret] !== undefined && this.fretPositions[fret + 1] !== undefined) {
                const currentFretX = -this.fretWidth/2 + this.fretPositions[fret];
                const nextFretX = -this.fretWidth/2 + this.fretPositions[fret + 1];
                track.width = nextFretX - currentFretX;
                track.x = currentFretX + track.width / 2;
            }

            this.noteTracks[fret] = track;
        }
    }

    getTrackColor(fret) {
        const colors = [
            0xff4444, 0xff8844, 0xffaa00, 0xffff00,
            0x88ff00, 0x00ff00, 0x00ff88, 0x00ffff,
            0x0088ff, 0x4444ff, 0x8844ff, 0xff44ff,
            0xff4488, 0xff4444, 0xff8844, 0xffaa00,
            0xffff00, 0x88ff00, 0x00ff00, 0x00ff88,
            0x00ffff, 0x0088ff, 0x4444ff, 0x8844ff
        ];
        return colors[fret % colors.length];
    }

    isStaticGameMode() {
        return getGamingMode() && getSpeedFactor() === 0;
    }

    layoutStaticGameNotes() {
        if (!this.isStaticGameMode()) {
            return;
        }

        const currentTime = Tone.now();
        const spacing = this.staticNoteSpacing;

        // Отбираем только активные ноты, не сыгранные и не пропущенные
        const playableNotes = this.movingNotes
            .filter(note => !note.hit && !note.missed && !note.falling)
            .sort((a, b) => {
                const timeA = a.originalTargetTime ?? 0;
                const timeB = b.originalTargetTime ?? 0;
                if (timeA !== timeB) {
                    return timeA - timeB;
                }
                return (a.sequenceIndex || 0) - (b.sequenceIndex || 0);
            });

        if (playableNotes.length === 0) {
            return;
        }

        let currentGroupKey = null;
        let groupIndex = -1;
        const layoutSpacing = this.staticNoteSpacing;

        playableNotes.forEach((note, index) => {
            const groupKey = note.originalTargetTime ?? note.sequenceIndex;
            if (currentGroupKey === null || groupKey !== currentGroupKey) {
                currentGroupKey = groupKey;
                groupIndex++;
            }

            const targetZ = -groupIndex * layoutSpacing;

            note.initialZ = targetZ;
            note.currentZ = targetZ;
            note.mesh.position.z = targetZ;
            note.staticLayoutZ = targetZ;

            const isFrontGroup = groupIndex === 0;

            note.startTime = currentTime;
            note.targetTime = isFrontGroup ? currentTime : Number.MAX_SAFE_INTEGER;
            note.totalTravelTime = isFrontGroup ? 0 : Number.MAX_SAFE_INTEGER;
            note.elapsedTime = 0;
            note.progress = isFrontGroup ? 1 : 0;
            note.displayStartTime = isFrontGroup ? currentTime : null;

            note.reachedFretboard = isFrontGroup;
            note.isWaiting = !isFrontGroup;

            // Создаем маркер пальца для нот на грифе (первая группа)
            if (isFrontGroup && 
                !note.fingerSphereCreated &&
                note.noteData && 
                note.noteData.finger !== undefined && 
                note.noteData.finger !== null) {
                const position = this.fretStringPositions[note.fret][note.string-1];
                if (position) {
                    const fingerCircle = this.createFingerCircle(
                        note.noteData.finger, 
                        position.x, 
                        position.y - 8,
                        note.string
                    );
                    if (fingerCircle) {
                        fingerCircle.visible = true;
                        this.scene.add(fingerCircle);
                        note.fingerSphere = fingerCircle;
                        note.fingerSphereCreated = true;
                        this.fingerSpheres.push({
                            sphere: fingerCircle,
                            string: note.string,
                            fret: note.fret,
                            finger: note.noteData.finger
                        });
                    }
                }
            }

            // Настраиваем визуальный акцент
            if (note.mesh) {
                note.mesh.visible = true;
                note.mesh.scale.set(1, 1, 1);

                note.mesh.children.forEach(child => {
                    if (child && child.material) {
                        child.material.opacity = isFrontGroup ? 1 : 0.5;
                        child.material.needsUpdate = true;
                    }
                });
            }
        });

        // Принудительно обновляем подсветку и дорожки
        this.lastHighlightUpdate = 0;
        this.lastTrackUpdate = 0;
        this.updateFretHighlighting();
        this.updateTrackPriorities();
    }

    // Метод для активации первой ноты в нулевом режиме
    activateFirstNote() {
        if (this.movingNotes.length === 0) return;

        if (this.isStaticGameMode()) {
            this.layoutStaticGameNotes();
            // Маркеры пальцев создаются в layoutStaticGameNotes для первой группы
            return;
        }

        // Находим первую ноту с максимальным временем (ожидающую активации)
        const firstNote = this.movingNotes.find(note =>
            note.targetTime === Number.MAX_SAFE_INTEGER
        );

        if (firstNote) {
            const currentTime = Tone.now();
            firstNote.targetTime = currentTime + 4.0; // 4 секунды до достижения грифа
            firstNote.totalTravelTime = 4.0;
            firstNote.initialZ = -4.0 * this.unitsPerSecond * 2;
            firstNote.currentZ = firstNote.initialZ;
            firstNote.mesh.position.z = firstNote.initialZ;
        }
    }

    // Метод для активации следующей ноты в нулевом режиме (пошаговое движение)
    // threejsBand.js - обновленный метод activateNextNote
    activateNextNote(currentNoteIndex) {
        if (this.movingNotes.length === 0) return;

        if (this.isStaticGameMode()) {
            this.layoutStaticGameNotes();
            return;
        }

        // В нулевом режиме: подвинуть все ожидающие ноты на 1 позицию вперед и остановить движение текущей
        const isGaming = getGamingMode();
        const speedFactor = getSpeedFactor();
        
        if (isGaming && speedFactor === 0) {
            // Останавливаем движение всех текущих активных нот (те, что уже на грифе)
            const currentTime = Tone.now();
            this.movingNotes.forEach(note => {
                if (!note.isWaiting && note.reachedFretboard) {
                    // Останавливаем движение - ноты остаются на грифе
                    note.totalTravelTime = Number.MAX_SAFE_INTEGER;
                    note.targetTime = Number.MAX_SAFE_INTEGER;
                }
            });

            // Активируем следующую ноту и перемещаем все ожидающие ноты на 1 позицию
            const nextNoteIndex = currentNoteIndex + 1;
            if (nextNoteIndex < this.movingNotes.length) {
                const nextNote = this.movingNotes[nextNoteIndex];

                // Проверяем, что нота еще не активирована
                if (nextNote.isWaiting) {
                    const travelTime = 4.0; // 4 секунды до грифа
                    nextNote.targetTime = currentTime + travelTime;
                    nextNote.totalTravelTime = travelTime;
                    
                    // Используем эффективную скорость для нулевого режима
                    const effectiveUnitsPerSecond = this.unitsPerSecond * 0.1;
                    nextNote.initialZ = -travelTime * effectiveUnitsPerSecond * 2;
                    nextNote.currentZ = nextNote.initialZ;
                    nextNote.mesh.position.z = nextNote.initialZ;
                    nextNote.isWaiting = false;
                    nextNote.startTime = currentTime;

                    // Делаем ноту полностью видимой
                    nextNote.mesh.children[0].material.opacity = 1;
                }

                // Сдвигаем все остальные ожидающие ноты на 1 позицию вперед (ближе к грифу)
                for (let i = nextNoteIndex + 1; i < this.movingNotes.length; i++) {
                    const waitingNote = this.movingNotes[i];
                    if (waitingNote.isWaiting) {
                        // Сдвигаем ноту на фиксированное расстояние вперед
                        const stepDistance = 8.0; // Расстояние на 1 позицию
                        waitingNote.currentZ += stepDistance;
                        waitingNote.mesh.position.z += stepDistance;
                    }
                }
            }
        } else {
            // Для других режимов - обычная логика (на случай, если метод вызывается не в нулевом режиме)
            const nextNoteIndex = currentNoteIndex + 1;
            if (nextNoteIndex < this.movingNotes.length) {
                const nextNote = this.movingNotes[nextNoteIndex];

                if (nextNote.isWaiting) {
                    const currentTime = Tone.now();
                    nextNote.targetTime = currentTime + 4.0;
                    nextNote.totalTravelTime = 4.0;
                    nextNote.initialZ = -4.0 * this.unitsPerSecond * 2;
                    nextNote.currentZ = nextNote.initialZ;
                    nextNote.mesh.position.z = nextNote.initialZ;
                    nextNote.isWaiting = false;

                    nextNote.mesh.children[0].material.opacity = 1;
                }
            }
        }
    }

    // Создание эффекта slideTo - линия скольжения между ладами
    createSlideEffect(noteData, startPosition, targetFret) {
        if (!this.fretStringPositions[targetFret]) return null;
        
        const targetPosition = this.fretStringPositions[targetFret][noteData.string - 1];
        if (!targetPosition) return null;
        
        // Создаём линию от текущего лада к целевому
        const points = [
            new THREE.Vector3(0, 0, 0), // Начальная точка (относительно ноты)
            new THREE.Vector3(
                targetPosition.x - startPosition.x,
                0,
                0
            )
        ];
        
        const lineGeometry = new THREE.BufferGeometry().setFromPoints(points);
        const lineMaterial = new THREE.LineBasicMaterial({
            color: 0x00ffff, // Cyan цвет для слайда
            transparent: true,
            opacity: 0.8,
            linewidth: 2
        });
        
        const line = new THREE.Line(lineGeometry, lineMaterial);
        line.position.y = 0.5; // Немного выше ноты
        
        // Создаём стрелку-указатель в конце линии
        // Используем геометрию из пула
        const arrowGeometry = this.geometryPool.cone;
        
        // НЕ переиспользуем материал - opacity может меняться при анимации
        const arrowMaterial = new THREE.MeshBasicMaterial({
            color: 0x00ffff,
            transparent: true,
            opacity: 0.9
        });
        const arrow = new THREE.Mesh(arrowGeometry, arrowMaterial);
        
        // Поворачиваем стрелку в направлении слайда
        const direction = targetPosition.x - startPosition.x;
        arrow.rotation.z = direction > 0 ? -Math.PI / 2 : Math.PI / 2;
        arrow.position.set(
            targetPosition.x - startPosition.x,
            0.5,
            0
        );
        
        return {
            line: line,
            arrow: arrow,
            targetFret: targetFret,
            startX: startPosition.x,
            targetX: targetPosition.x,
            animationPhase: 0
        };
    }
    
    // Создание эффекта hammer-on - дуга от текущей ноты до следующей
    createHammerEffect(noteData, position, nextNoteData = null) {
        // Определяем расстояние до следующей ноты
        let arcWidth = 1.0; // По умолчанию
        let targetX = 0;
        
        if (nextNoteData && nextNoteData.fret !== undefined) {
            // Находим позицию следующей ноты
            const nextPosition = this.fretStringPositions[nextNoteData.fret] 
                ? this.fretStringPositions[nextNoteData.fret][nextNoteData.string - 1]
                : null;
            
            if (nextPosition) {
                // Расстояние между нотами
                arcWidth = Math.abs(nextPosition.x - position.x);
                targetX = nextPosition.x - position.x;
            }
        }
        
        // Минимальная ширина дуги
        arcWidth = Math.max(arcWidth, 0.8);
        
        // Создаём дугу от текущей ноты до следующей
        // Центр дуги - посередине между нотами
        const centerX = targetX / 2;
        const arcHeight = Math.min(1.0, arcWidth * 0.5); // Высота пропорциональна ширине
        
        const curve = new THREE.EllipseCurve(
            centerX, 0.6, // центр (смещён к середине между нотами)
            arcWidth / 2, arcHeight, // радиусы X и Y
            0, Math.PI, // углы (полукруг)
            false, // по часовой
            0 // поворот
        );
        
        const points = curve.getPoints(20);
        const points3D = points.map(p => new THREE.Vector3(p.x, p.y, 0));
        
        const arcGeometry = new THREE.BufferGeometry().setFromPoints(points3D);
        const arcMaterial = new THREE.LineBasicMaterial({
            color: 0xff8800, // Оранжевый цвет для hammer
            transparent: true,
            opacity: 0.9,
            linewidth: 3
        });
        
        const arc = new THREE.Line(arcGeometry, arcMaterial);
        
        // Добавляем букву "H" в центре дуги
        const hammerLabel = this.createEffectLabel('H', 0xff8800);
        hammerLabel.position.set(centerX, 0.6 + arcHeight + 0.3, 0);
        
        const hammerGroup = new THREE.Group();
        hammerGroup.add(arc);
        hammerGroup.add(hammerLabel);
        
        // Сохраняем данные для анимации
        hammerGroup.userData = {
            targetX: targetX,
            arcWidth: arcWidth
        };
        
        return hammerGroup;
    }
    
    // Создание текстовой метки для эффекта
    createEffectLabel(text, color) {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = 64;
        canvas.height = 64;
        
        context.clearRect(0, 0, canvas.width, canvas.height);
        context.font = 'Bold 40px Arial';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        
        // Преобразуем цвет в строку
        const r = (color >> 16) & 255;
        const g = (color >> 8) & 255;
        const b = color & 255;
        context.fillStyle = `rgb(${r}, ${g}, ${b})`;
        context.fillText(text, 32, 32);
        
        const texture = new THREE.CanvasTexture(canvas);
        const material = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            opacity: 0.9
        });
        
        const sprite = new THREE.Sprite(material);
        sprite.scale.set(0.5, 0.5, 1);
        
        return sprite;
    }
    
    // Анимация эффекта slideTo
    animateSlideEffect(note, currentTime, progress) {
        const effect = note.slideEffect;
        if (!effect || !effect.line) return;
        
        // Пульсация линии слайда
        const pulse = 0.7 + 0.3 * Math.sin(currentTime * 8);
        effect.line.material.opacity = pulse;
        
        // Анимация "бегущего" эффекта по линии
        if (note.reachedFretboard) {
            // Когда нота на грифе - анимируем переход к целевому ладу
            note.slideAnimationProgress = Math.min(1, (note.slideAnimationProgress || 0) + 0.05);
            
            // Анимируем движение стрелки
            if (effect.arrow) {
                const moveProgress = note.slideAnimationProgress;
                const startX = 0;
                const endX = effect.targetX - effect.startX;
                
                // Перемещаем стрелку
                effect.arrow.position.x = startX + (endX - startX) * moveProgress;
                
                // Свечение стрелки
                effect.arrow.material.opacity = 0.6 + 0.4 * Math.sin(currentTime * 15);
                
                // Масштаб стрелки для эффекта "удара"
                const arrowScale = 1 + 0.2 * Math.sin(moveProgress * Math.PI);
                effect.arrow.scale.set(arrowScale, arrowScale, arrowScale);
            }
            
            // Изменяем цвет линии при завершении
            if (note.slideAnimationProgress > 0.8) {
                effect.line.material.color.setHex(0x00ff00); // Зелёный при завершении
            }
        } else {
            // Пока нота движется к грифу - линия пульсирует
            const glowIntensity = 0.5 + 0.5 * progress;
            effect.line.material.opacity = glowIntensity * pulse;
            
            if (effect.arrow) {
                effect.arrow.material.opacity = glowIntensity;
            }
        }
    }
    
    // Обновление hammer эффектов - создаёт эффекты для нот у которых они ещё не созданы
    // Вызывать после добавления всех нот в movingNotes
    updateHammerEffects() {
        this.movingNotes.forEach((note, index) => {
            // Пропускаем если нет hammer или эффект уже создан
            if (!note.noteData.effect || !note.noteData.effect.hammer) return;
            if (note.hammerEffect) return;
            
            // Ищем следующую ноту на той же струне в movingNotes
            let nextNote = null;
            for (let i = index + 1; i < this.movingNotes.length; i++) {
                const candidate = this.movingNotes[i];
                if (candidate.string === note.string && !candidate.noteData.pause) {
                    nextNote = candidate;
                    break;
                }
            }
            
            if (nextNote) {
                const position = { x: note.startX, y: note.startY + 8 };
                const nextNoteData = nextNote.noteData;
                
                const hammerEffect = this.createHammerEffect(note.noteData, position, nextNoteData);
                if (hammerEffect) {
                    note.mesh.add(hammerEffect);
                    note.hammerEffect = hammerEffect;
                }
            }
        });
    }

    // Анимация эффекта hammer-on
    animateHammerEffect(note, currentTime, progress) {
        const effect = note.hammerEffect;
        if (!effect) return;
        
        // Пульсация hammer эффекта
        const pulse = 0.6 + 0.4 * Math.sin(currentTime * 10);
        
        // Анимация "удара" - подпрыгивание дуги
        const bounceHeight = 0.1 * Math.sin(currentTime * 12);
        effect.position.y = bounceHeight;
        
        // Изменение прозрачности в зависимости от прогресса
        effect.children.forEach(child => {
            if (child.material) {
                child.material.opacity = pulse * (0.5 + 0.5 * progress);
            }
        });
        
        // При достижении грифа - эффект "удара"
        if (note.reachedFretboard) {
            const hitTime = currentTime - note.displayStartTime;
            
            if (hitTime < 0.3) {
                // Быстрое расширение и сжатие
                const hitScale = 1 + 0.5 * Math.sin(hitTime * 30);
                effect.scale.set(hitScale, hitScale, hitScale);
                
                // Яркая вспышка
                effect.children.forEach(child => {
                    if (child.material) {
                        child.material.opacity = Math.min(1, pulse + 0.3);
                        if (child.material.color) {
                            // Временно делаем ярче
                            child.material.color.setHex(0xffaa00);
                        }
                    }
                });
            } else {
                effect.scale.set(1, 1, 1);
                // Возвращаем нормальный цвет
                effect.children.forEach(child => {
                    if (child.material && child.material.color) {
                        child.material.color.setHex(0xff8800);
                    }
                });
            }
        }
    }

    // Создание эффекта bend - стрелка вверх с кривой
    createBendEffect(noteData, position) {
        const bendData = noteData.effect.bend;
        if (!bendData || !bendData.points) return null;
        
        const bendGroup = new THREE.Group();
        
        // Максимальное значение bend для масштабирования
        const maxValue = bendData.value || 0.04;
        const bendHeight = Math.max(0.5, maxValue * 25); // Масштабируем высоту
        
        // Создаём кривую bend (дуга вверх)
        const curve = new THREE.QuadraticBezierCurve3(
            new THREE.Vector3(0, 0, 0),           // Начало
            new THREE.Vector3(0, bendHeight, 0),   // Контрольная точка (верх)
            new THREE.Vector3(0.3, bendHeight * 0.7, 0)  // Конец (немного вправо)
        );
        
        const points = curve.getPoints(20);
        const curveGeometry = new THREE.BufferGeometry().setFromPoints(points);
        const curveMaterial = new THREE.LineBasicMaterial({
            color: 0xff00ff, // Фиолетовый цвет для bend
            transparent: true,
            opacity: 0.9,
            linewidth: 3
        });
        
        const curveLine = new THREE.Line(curveGeometry, curveMaterial);
        bendGroup.add(curveLine);
        
        // Создаём стрелку вверх
        // Используем геометрию из пула (меньшую для bend)
        if (!this.geometryPool.coneBend) {
            this.geometryPool.coneBend = new THREE.ConeGeometry(0.15, 0.3, 8);
        }
        const arrowGeometry = this.geometryPool.coneBend;
        
        // НЕ переиспользуем материал - opacity может меняться при анимации
        const arrowMaterial = new THREE.MeshBasicMaterial({
            color: 0xff00ff,
            transparent: true,
            opacity: 0.9
        });
        const arrow = new THREE.Mesh(arrowGeometry, arrowMaterial);
        arrow.position.set(0.3, bendHeight * 0.7, 0);
        bendGroup.add(arrow);
        
        // Добавляем метку "B" (bend)
        const bendLabel = this.createEffectLabel('B', 0xff00ff);
        bendLabel.position.set(0.5, bendHeight * 0.5, 0);
        bendGroup.add(bendLabel);
        
        // Сохраняем данные для анимации
        bendGroup.userData = {
            bendData: bendData,
            maxHeight: bendHeight,
            currentValue: 0,
            animationProgress: 0
        };
        
        return bendGroup;
    }
    
    // Анимация эффекта bend
    animateBendEffect(note, currentTime, progress) {
        const effect = note.bendEffect;
        if (!effect || !effect.userData) return;
        
        const bendData = effect.userData.bendData;
        if (!bendData || !bendData.points) return;
        
        // При достижении грифа - анимируем bend
        if (note.reachedFretboard) {
            // Время с момента касания грифа
            const bendTime = currentTime - note.displayStartTime;
            
            // Находим текущее значение bend на основе времени и точек
            const points = bendData.points;
            const maxPosition = Math.max(...points.map(p => p.position));
            
            // Конвертируем время в позицию (position в points - это условные единицы времени)
            // Предполагаем что весь bend занимает ~1 секунду
            const bendDuration = 1.0; // секунды на весь bend цикл
            const currentPosition = (bendTime / bendDuration) * maxPosition;
            
            // Интерполируем значение bend между точками
            let currentBendValue = 0;
            for (let i = 0; i < points.length - 1; i++) {
                if (currentPosition >= points[i].position && currentPosition <= points[i + 1].position) {
                    const t = (currentPosition - points[i].position) / (points[i + 1].position - points[i].position);
                    currentBendValue = points[i].value + t * (points[i + 1].value - points[i].value);
                    break;
                }
            }
            // Если вышли за последнюю точку - берём последнее значение
            if (currentPosition > points[points.length - 1].position) {
                currentBendValue = points[points.length - 1].value;
            }
            
            // Умножаем на 10 для более заметного эффекта
            const bendMultiplier = 20;
            const bendOffset = currentBendValue * bendMultiplier;
            
            // ДВИГАЕМ САМ МАРКЕР НОТЫ ВВЕРХ
            // Сохраняем базовую Y позицию если ещё не сохранили
            if (note.baseMeshY === undefined) {
                note.baseMeshY = note.mesh.position.y;
            }
            note.mesh.position.y = note.baseMeshY + bendOffset;
            
            // Анимируем визуальный эффект bend (стрелку)
            effect.position.y = bendOffset * 0.5;
            
            // Масштабируем эффект в зависимости от интенсивности
            const maxValue = bendData.value || 0.04;
            const normalizedValue = maxValue > 0 ? currentBendValue / maxValue : 0;
            const scale = 0.5 + normalizedValue * 0.5;
            effect.scale.set(scale, scale, scale);
            
            // Пульсация
            const pulse = 0.7 + 0.3 * Math.sin(currentTime * 8);
            
            // Изменяем прозрачность и цвет
            effect.children.forEach(child => {
                if (child.material) {
                    child.material.opacity = pulse * (0.5 + normalizedValue * 0.5);
                    
                    if (child.material.color) {
                        const intensity = Math.floor(normalizedValue * 100);
                        child.material.color.setHex(0xff00ff + intensity * 0x000100);
                    }
                }
            });
            
            // Небольшая вибрация при подтяжке
            if (normalizedValue > 0) {
                const vibration = 0.02 * Math.sin(currentTime * 25) * normalizedValue;
                effect.position.x = vibration;
            }
        } else {
            // Пока нота движется к грифу - просто пульсация эффекта
            const pulse = 0.5 + 0.5 * Math.sin(currentTime * 6);
            effect.children.forEach(child => {
                if (child.material) {
                    child.material.opacity = 0.5 * pulse;
                }
            });
        }
    }

    // Обновленный метод добавления движущейся ноты
    // Обновленный метод addMovingNote для правильного позиционирования
    // Обновленный метод addMovingNote с поддержкой режима ожидания
    // threejsBand.js - исправленный метод addMovingNote
    // nextNoteData - данные следующей ноты для эффекта hammer
    addMovingNote(noteData, startTime, targetTime, activated = true, nextNoteData = null) {
        if (!this.scene) return null;

        const track = this.noteTracks[noteData.fret];
        if (!track) {
            return null;
        }

        // Увеличиваем счетчик нот для этого лада
        this.fretNoteCounts[noteData.fret]++;

        // Показываем трек
        this.updateTrackVisibility(noteData.fret);
        this.addActiveFret(noteData.fret);

        const position = this.fretStringPositions[noteData.fret][noteData.string-1];
        if (!position) {
            return null;
        }

        // Создаем сферу для ноты
        // Используем геометрию из пула
        const noteGeometry = this.geometryPool.sphere;
        
        // НЕ переиспользуем материал для нот
        // Все ноты создаются полностью непрозрачными для устранения моргания струн
        const noteMaterial = new THREE.MeshBasicMaterial({
            color: this.getStringColor(noteData.string),
            transparent: false, // Делаем непрозрачными
            opacity: 1
        });

        const noteMesh = new THREE.Mesh(noteGeometry, noteMaterial);
        noteMesh.renderOrder = 0; // Обычный порядок отрисовки
        
        const noteGroup = new THREE.Group();
        noteGroup.add(noteMesh);
        
        // Убеждаемся, что ноты видимы по умолчанию
        noteGroup.visible = true;
        noteMesh.visible = true;

        // Сферу пальца создадим позже, когда нота будет близка к грифу
        // Не создаем сразу, чтобы не рисовать все сферы одновременно

        const currentTime = Tone.now();
        const isStaticMode = this.isStaticGameMode();
        let initialZ, timeUntilTarget;

        // Используем переданный startTime для правильного расчета initialZ
        // startTime может быть в прошлом (для создания нот с большим initialZ)
        // Это нормально и нужно для равномерной скорости всех нот
        const actualStartTime = startTime || currentTime;
        
        // Получаем фактор скорости для игрового режима
        const speedFactor = getGamingMode() ? (getSpeedFactor() || 1) : 1;
        // Скорость полета нот НЕ зависит от speedFactor - ноты всегда летят одинаково
        // speedFactor влияет только на длительность нахождения на грифе
        const effectiveUnitsPerSecond = this.unitsPerSecond;

        // Расчет времени до цели:
        // ВСЕ ноты создаются одновременно (currentTime)
        // initialZ рассчитывается из времени до targetTime от момента создания
        timeUntilTarget = Math.max(0, targetTime - currentTime);
        
        // Счетчик для отладки (используется в других модулях)
        if (!window._noteDebugCounter) window._noteDebugCounter = 0;
        window._noteDebugCounter++;
        
        // Ограничиваем визуальную дистанцию, но сохраняем оригинальный targetTime для синхронизации
        if (timeUntilTarget > TIME_CONSTANTS.MAX_TRAVEL_TIME) {
            timeUntilTarget = TIME_CONSTANTS.MAX_TRAVEL_TIME;
        }
        
        // Нормализуем время движения
        timeUntilTarget = normalizeTravelTime(timeUntilTarget);
        
        if (isStaticMode) {
            initialZ = 0;
        } else {
            initialZ = calculateInitialZPosition(timeUntilTarget, effectiveUnitsPerSecond);
        }


        noteGroup.position.set(
            position.x,
            position.y - 8,
            initialZ
        );

        const durationMs = calculateDurationMs(noteData);
        const durationSeconds = durationMs / 1000;

        // Вычисляем реальную длительность с учётом коэффициента скорости
        // Нота должна находиться на грифе durationSeconds / speedFactor времени
        const adjustedDurationSeconds = speedFactor > 0 
            ? durationSeconds / speedFactor 
            : durationSeconds;
        
        const originalTargetTime = targetTime;

        // Создаём визуальные эффекты для slideTo и hammer
        let slideEffect = null;
        let hammerEffect = null;
        
        // Эффект slideTo - линия скольжения к целевому ладу
        if (noteData.effect && noteData.effect.slideTo && typeof noteData.effect.slideTo === 'number') {
            slideEffect = this.createSlideEffect(noteData, position, noteData.effect.slideTo);
            if (slideEffect) {
                noteGroup.add(slideEffect.line);
                if (slideEffect.arrow) noteGroup.add(slideEffect.arrow);
            }
        }
        
        // Эффект hammer-on - дуга от текущей ноты до следующей
        // Сохраняем флаг hammer для последующего создания эффекта, когда все ноты добавлены
        const hasHammer = noteData.effect && noteData.effect.hammer;
        if (hasHammer) {
            // Если nextNoteData передан - создаём эффект сразу
            if (nextNoteData) {
                hammerEffect = this.createHammerEffect(noteData, position, nextNoteData);
                if (hammerEffect) {
                    noteGroup.add(hammerEffect);
                }
            }
            // Иначе эффект будет создан позже через updateHammerEffects()
        }
        
        // Эффект bend - стрелка вверх с анимацией подтяжки
        let bendEffect = null;
        if (noteData.effect && noteData.effect.bend && noteData.effect.bend !== false) {
            bendEffect = this.createBendEffect(noteData, position);
            if (bendEffect) {
                noteGroup.add(bendEffect);
            }
        }

        const movingNote = {
            mesh: noteGroup,
            track: track,
            noteData: noteData,
            startTime: actualStartTime, // Время для расчета initialZ (может быть в прошлом)
            creationTime: currentTime, // Реальный момент создания ноты (для расчета движения)
            targetTime: isStaticMode ? Number.MAX_SAFE_INTEGER : targetTime,
            duration: durationMs,
            initialZ: initialZ,
            currentZ: initialZ,
            targetZ: 0,
            durationSeconds: adjustedDurationSeconds, // Используем скорректированную длительность
            reachedFretboard: false,
            displayStartTime: null,
            hit: false,
            missed: false,
            string: noteData.string,
            fret: noteData.fret,
            startX: position.x,
            startY: position.y - 8,
            totalTravelTime: isStaticMode ? Number.MAX_SAFE_INTEGER : timeUntilTarget,
            elapsedTime: 0,
            progress: isStaticMode ? 0 : 0, // Инициализируем прогресс
            isGameBlockNote: true,
            isWaiting: isStaticMode ? true : !activated, // Флаг ожидания активации
            sequenceIndex: this.noteSequenceCounter++,
            originalTargetTime: originalTargetTime,
            staticLayoutZ: initialZ,
            speedFactor: speedFactor, // Сохраняем фактор скорости для отладки
            fingerSphere: null, // Создадим позже, когда нота будет близка к грифу
            fingerSphereCreated: false, // Флаг, что сфера еще не создана
            slideEffect: slideEffect, // Эффект slideTo
            hammerEffect: hammerEffect, // Эффект hammer
            bendEffect: bendEffect, // Эффект bend
            slideAnimationProgress: 0, // Прогресс анимации слайда
            bendAnimationProgress: 0 // Прогресс анимации bend
        };

        this.scene.add(noteGroup);
        this.movingNotes.push(movingNote);

        return movingNote;
    }

    addStaticBlockNotes(blockMeasures, tempo = 120) {
        const notesBatch = [];

        blockMeasures.forEach(measure => {
            measure.forEach(chordGroup => {
                const isPause = chordGroup[0] && chordGroup[0].pause === "true";
                if (!isPause) {
                    chordGroup.forEach(noteData => {
                        notesBatch.push(noteData);
                    });
                }
            });
        });

        if (notesBatch.length === 0) {
            return;
        }

        // Добавляем все ноты сразу, но помечаем, что они в ожидании
        // Для hammer эффекта передаём следующую ноту на той же струне
        const addedNotes = [];
        notesBatch.forEach((noteData, index) => {
            // Ищем следующую ноту на той же струне для эффекта hammer
            let nextNoteOnString = null;
            if (noteData.effect && noteData.effect.hammer) {
                for (let i = index + 1; i < notesBatch.length; i++) {
                    if (notesBatch[i].string === noteData.string) {
                        nextNoteOnString = notesBatch[i];
                        break;
                    }
                }
            }
            
            const movingNote = this.addMovingNote(noteData, 0, 0, false, nextNoteOnString);
            if (movingNote) {
                addedNotes.push(movingNote);
            }
        });

        // Присваиваем последовательность с учётом исходного порядка
        addedNotes.forEach((note, index) => {
            note.sequenceIndex = index;
            note.originalTargetTime = index;
            note.staticLayoutZ = note.currentZ;
        });

        this.layoutStaticGameNotes();
        setTimeout(() => this.activateFirstNote(), 100);
    }

    // Обновление видимости трека на основе количества нот
    updateTrackVisibility(fret) {
        const track = this.fretTracks[fret];
        if (track) {
            track.visible = this.fretNoteCounts[fret] > 0;
        }
    }

    // Управление активными ладами для фокусировки камеры
    addActiveFret(fret) {
        this.activeFrets.add(fret);
        // Камера теперь обновляется автоматически в updateMovingNotes через updateCameraToFollowNotes
        // this.updateCameraFocus();
        this.updateFretHighlight(fret, true);
    }

    removeActiveFret(fret) {
        // Уменьшаем счетчик нот для этого лада
        this.fretNoteCounts[fret] = Math.max(0, this.fretNoteCounts[fret] - 1);

        // Обновляем видимость трека
        this.updateTrackVisibility(fret);

        // Если нот на ладу больше нет, убираем из активных
        if (this.fretNoteCounts[fret] === 0) {
            this.activeFrets.delete(fret);
            // Камера теперь обновляется автоматически в updateMovingNotes через updateCameraToFollowNotes
            // this.updateCameraFocus();
            this.updateFretHighlight(fret, false);
        }
    }

    // Обновленная фокусировка камеры на падающих нотах
    updateCameraToFollowNotes() {
        // В статичном режиме (speedFactor === 0) используем ноты на грифе
        if (this.isStaticGameMode()) {
            const notesOnFretboard = this.movingNotes.filter(note => 
                note.reachedFretboard && 
                !note.hit && 
                !note.missed
            );
            
            if (notesOnFretboard.length > 0) {
                const frets = notesOnFretboard.map(n => n.fret);
                this.updateCameraFocus(frets);
            }
            return;
        }

        // Получаем активные движущиеся ноты (не достигшие грифа)
        const activeMovingNotes = this.movingNotes.filter(note => 
            !note.reachedFretboard && 
            !note.isWaiting && 
            note.currentZ > -50 &&
            note.currentZ < 20
        );

        // Если нет активных нот, используем ноты на грифе для фокусировки
        if (activeMovingNotes.length === 0) {
            const notesOnFretboard = this.movingNotes.filter(note => 
                note.reachedFretboard && 
                !note.hit && 
                !note.missed
            );
            
            if (notesOnFretboard.length > 0) {
                const frets = notesOnFretboard.map(n => n.fret);
                this.updateCameraFocus(frets);
            } else {
                this.resetCamera();
            }
            return;
        }

        // Вычисляем средние координаты всех активных нот
        let sumX = 0;
        let sumZ = 0;
        let minX = Infinity;
        let maxX = -Infinity;

        activeMovingNotes.forEach(note => {
            const noteX = note.mesh.position.x;
            sumX += noteX;
            sumZ += note.currentZ;
            minX = Math.min(minX, noteX);
            maxX = Math.max(maxX, noteX);
        });

        const avgX = sumX / activeMovingNotes.length;
        const avgZ = sumZ / activeMovingNotes.length;
        const width = maxX - minX;

        // Вычисляем позицию камеры для центрирования нот
        // Камера должна быть на уровне средней Z-позиции нот, но с отступом
        const targetX = avgX;
        
        // Динамический расчет Z-позиции камеры на основе:
        // 1. Расстояния до нот (avgZ)
        // 2. Ширины охвата нот (width)
        // 3. Мобильного устройства (более близкая камера для мобильных)
        const baseDistance = 25;
        const widthFactor = Math.max(10, width * 2);
        const zOffsetFromNotes = Math.max(12, avgZ + baseDistance);
        
        // На мобильных устройствах камера ближе и более отзывчива
        const mobileFactor = this.isMobileDevice ? 0.8 : 1.0;
        
        const targetZ = Math.min(35, Math.max(15, zOffsetFromNotes * mobileFactor));

        // Используем быструю анимацию для отслеживания нот в реальном времени
        this.animateCameraToNotes(targetX, targetZ, 100);
    }

    // Быстрая анимация камеры для следования за нотами
    animateCameraToNotes(targetX, targetZ, duration = 100) {
        // Для плавного следования используем более простую анимацию
        const currentX = this.camera.position.x;
        const currentZ = this.camera.position.z;
        
        // На мобильных устройствах камера более отзывчива
        const smoothFactor = this.isMobileDevice ? 0.25 : 0.15; // Чем больше, тем быстрее следование
        const newX = currentX + (targetX - currentX) * smoothFactor;
        const newZ = currentZ + (targetZ - currentZ) * smoothFactor;
        
        this.camera.position.x = newX;
        this.camera.position.z = newZ;
        this.camera.lookAt(newX, -2, 0);
        
        this.lastCameraPosition = { x: newX, z: newZ };
    }

    // Обновленная фокусировка камеры
    updateCameraFocus(targetFrets) {
        if (!targetFrets || targetFrets.length === 0) {
            this.resetCamera();
            return;
        }

        const fretsArray = Array.isArray(targetFrets) ? targetFrets : Array.from(targetFrets);
        const minFret = Math.min(...fretsArray);
        const maxFret = Math.max(...fretsArray);

        // Добавляем отступы
        const padding = 1;
        const visibleMinFret = Math.max(0, minFret - padding);
        const visibleMaxFret = Math.min(this.fretCount - 1, maxFret + padding);

        // Вычисляем позиции для видимых ладов
        const startX = -this.fretWidth/2 + this.fretPositions[visibleMinFret];
        const endX = -this.fretWidth/2 + this.fretPositions[visibleMaxFret + 1];
        const centerX = (startX + endX) / 2;
        const width = endX - startX;

        // Вычисляем новую позицию камеры
        const targetX = centerX;
        const targetZ = Math.max(15, Math.min(30, 25 * (20 / Math.max(width, 10))));

        this.animateCameraSmooth(targetX, targetZ, 1000);
    }

    // В threejsBand.js - улучшенный метод фокусировки
    animateCameraToBlock(blockNotes, duration = 2000) {
        if (this.cameraAnimation) {
            cancelAnimationFrame(this.cameraAnimation);
        }

        if (!blockNotes || blockNotes.length === 0) {
            // Если нот нет, оставляем камеру на последней позиции
            return;
        }

        // Находим минимальный и максимальный лад в блоке
        const frets = blockNotes.map(note => note.fret).filter(fret => fret !== undefined);
        if (frets.length === 0) return;

        const minFret = Math.min(...frets);
        const maxFret = Math.max(...frets);

        // Добавляем отступы для лучшего обзора
        const padding = Math.max(2, Math.ceil((maxFret - minFret) * 0.2));
        const visibleMinFret = Math.max(0, minFret - padding);
        const visibleMaxFret = Math.min(this.fretCount - 1, maxFret + padding);

        // Вычисляем позиции для видимых ладов
        const startX = -this.fretWidth/2 + this.fretPositions[visibleMinFret];
        const endX = -this.fretWidth/2 + this.fretPositions[visibleMaxFret + 1];
        const centerX = (startX + endX) / 2;
        const width = endX - startX;

        // Вычисляем новую позицию камеры
        const targetX = centerX;
        const targetZ = Math.max(15, Math.min(35, 30 * (25 / Math.max(width, 15))));

        this.animateCameraSmooth(targetX, targetZ, duration);
    }

    // Плавная анимация камеры
    animateCameraSmooth(targetX, targetZ, duration = 2000) {
        if (this.cameraAnimation) {
            cancelAnimationFrame(this.cameraAnimation);
        }

        this.lastCameraPosition = { x: targetX, z: targetZ };

        const startX = this.camera.position.x;
        const startZ = this.camera.position.z;
        const startTime = Date.now();

        const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // Плавная easing функция (easeInOutCubic)
            const easeProgress = progress < 0.5
                ? 4 * progress * progress * progress
                : 1 - Math.pow(-2 * progress + 2, 3) / 2;

            this.camera.position.x = startX + (targetX - startX) * easeProgress;
            this.camera.position.z = startZ + (targetZ - startZ) * easeProgress;
            this.camera.lookAt(this.camera.position.x, -2, 0);

            if (progress < 1) {
                this.cameraAnimation = requestAnimationFrame(animate);
            } else {
                this.cameraAnimation = null;
            }
        };

        animate();
    }

    // Анимация перемещения камеры
    animateCameraTo(targetX, targetZ) {
        if (this.cameraAnimation) {
            cancelAnimationFrame(this.cameraAnimation);
        }

        const startX = this.camera.position.x;
        const startZ = this.camera.position.z;
        const startTime = Date.now();
        const duration = 500; // ms

        const animate = () => {
            const elapsed = Date.now() - startTime;
            const progress = Math.min(elapsed / duration, 1);

            // Плавная easing функция
            const easeProgress = 1 - Math.pow(1 - progress, 3);

            this.camera.position.x = startX + (targetX - startX) * easeProgress;
            this.camera.position.z = startZ + (targetZ - startZ) * easeProgress;
            this.camera.lookAt(this.camera.position.x, -2, 0);

            if (progress < 1) {
                this.cameraAnimation = requestAnimationFrame(animate);
            } else {
                this.cameraAnimation = null;
            }
        };

        animate();
    }

    // Сброс камеры в исходное положение
    resetCamera() {
        // Не сбрасываем камеру в центр, оставляем на последней позиции
        // или используем сохраненную позицию
        if (this.lastCameraPosition) {
            this.animateCameraSmooth(
                this.lastCameraPosition.x,
                this.lastCameraPosition.z,
                1000
            );
        }
    }

    // Подсветка активных ладов в цвет трека
    updateFretHighlight(fret, show) {
        if (this.fretHighlights[fret]) {
            this.fretHighlights[fret].visible = show;
        }
    }

    // Обновленная подсветка ладов
    // Оптимизированный метод updateFretHighlighting
    updateFretHighlighting() {
        // Кэшируем активные ноты
        const activeNotes = this.movingNotes.filter(note =>
            note.reachedFretboard && !note.hit && !note.missed
        );

        // Определяем, какие лады должны быть подсвечены
        const fretsToHighlight = new Set();
        const notesByFret = {}; // Группируем ноты по ладам для создания сфер пальцев
        
        if (activeNotes.length > 0) {
            // Для аккордов: группируем ноты по времени отображения
            const notesByDisplayTime = {};
            activeNotes.forEach(note => {
                const displayTime = note.displayStartTime || 0;
                if (!notesByDisplayTime[displayTime]) {
                    notesByDisplayTime[displayTime] = [];
                }
                notesByDisplayTime[displayTime].push(note);
            });

            // Находим группу нот с самым поздним временем отображения
            const latestTime = Math.max(...Object.keys(notesByDisplayTime).map(Number));
            const latestNotes = notesByDisplayTime[latestTime];

            // Собираем лады для подсветки и группируем ноты по ладам
            latestNotes.forEach(note => {
                fretsToHighlight.add(note.fret);
                if (!notesByFret[note.fret]) {
                    notesByFret[note.fret] = [];
                }
                notesByFret[note.fret].push(note);
            });

            this.highlightedFret = latestNotes[0]?.fret || null;
        } else {
            this.highlightedFret = null;
        }

        // Батчим обновления видимости (обновляем только изменившиеся)
        for (let i = 0; i < this.fretHighlights.length; i++) {
            const highlight = this.fretHighlights[i];
            if (highlight) {
                const shouldBeVisible = fretsToHighlight.has(i);
                if (highlight.visible !== shouldBeVisible) {
                    highlight.visible = shouldBeVisible;
                    
                    // Маркеры пальцев теперь создаются до достижения грифа и удаляются вместе с нотами
                }
            }
        }
    }

    // Создание подсветки для ладов в цвет треков
    createFretHighlights() {
        const highlightGroup = new THREE.Group();

        for (let fret = 0; fret < this.fretCount; fret++) {
            const currentFretX = -this.fretWidth/2 + this.fretPositions[fret];
            const nextFretX = -this.fretWidth/2 + this.fretPositions[fret + 1];
            const trackWidth = nextFretX - currentFretX;

            const highlightGeometry = new THREE.PlaneGeometry(trackWidth, this.fretHeight);
            const highlightMaterial = new THREE.MeshBasicMaterial({
                color: this.getTrackColor(fret), // Используем цвет трека
                transparent: true,
                opacity: 0.3, // Немного увеличим прозрачность для лучшей видимости
                side: THREE.DoubleSide
            });

            const highlight = new THREE.Mesh(highlightGeometry, highlightMaterial);
            const xPosition = currentFretX + trackWidth / 2;
            const yPosition = -8; // Совпадает с позицией грифа

            highlight.position.set(xPosition, yPosition, 0.15);
            highlight.visible = false; // По умолчанию скрыты

            highlightGroup.add(highlight);
            this.fretHighlights[fret] = highlight;
        }

        this.scene.add(highlightGroup);
    }

    // Показать трек
    showTrack(fret) {
        const track = this.fretTracks[fret];
        if (track) {
            track.visible = true;
        }
    }

    // Скрыть трек
    hideTrack(fret) {
        const track = this.fretTracks[fret];
        if (track) {
            track.visible = false;
        }
    }

    // Оптимизированный метод скрытия всех треков
    hideAllTracks() {
        for (let i = 0; i < this.fretCount; i++) {
            const track = this.fretTracks[i];
            if (track && track.visible) {
                track.visible = false;
            }
            this.fretNoteCounts[i] = 0; // Сбрасываем счетчики
        }
    }

    // Обновленный метод обновления движущихся нот
    // Оптимизированный метод updateMovingNotes
    updateMovingNotes() {
        const currentTime = Tone.now();
        const notesToRemove = [];
        let needsSort = false;
        let needsHighlightUpdate = false;
        let needsTrackUpdate = false;

        // Сортируем ноты реже (не каждый кадр)
        const timeSinceLastSort = currentTime * 1000 - this.lastSortTime;
        if (getGamingMode() && timeSinceLastSort > this.sortInterval) {
            this.movingNotes.sort((a, b) => b.currentZ - a.currentZ);
            this.lastSortTime = currentTime * 1000;
            needsTrackUpdate = true;
        }

        // Итерируем в обратном порядке для безопасного удаления
        for (let i = this.movingNotes.length - 1; i >= 0; i--) {
            const note = this.movingNotes[i];
            
            // Пропускаем ноты в режиме ожидания
            if (note.isWaiting) {
                continue;
            }

            // Сохраняем старое состояние для оптимизации
            const oldReachedFretboard = note.reachedFretboard;
            const oldProgress = note.progress || 0;

            // Обновляем прошедшее время от момента создания ноты
            note.elapsedTime = Math.max(0, currentTime - note.creationTime);

            // Расчет прогресса движения (от 0 до 1)
            // ВАЖНО: Используем реальное время до targetTime для правильной синхронизации
            let progress = 0;
            
            // Рассчитываем сколько времени нота должна лететь от момента создания до targetTime
            const realTimeUntilTarget = Math.max(0, note.targetTime - note.creationTime);
            
            if (realTimeUntilTarget > 0 && realTimeUntilTarget < Number.MAX_SAFE_INTEGER) {
                // Нормальный расчет прогресса на основе реального времени до цели
                progress = Math.min(note.elapsedTime / realTimeUntilTarget, 1);
            } else if (realTimeUntilTarget === 0) {
                // Если время движения равно 0, ноты должны быть на грифе сразу
                progress = 1;
            } else if (realTimeUntilTarget >= Number.MAX_SAFE_INTEGER) {
                // Ноты в режиме ожидания - не двигаются
                progress = 0;
            } else {
                // Fallback: если elapsedTime > 0, но realTimeUntilTarget некорректен
                progress = note.elapsedTime > 0 ? 1 : 0;
            }
            
            note.progress = progress;

            if (!note.reachedFretboard) {
                // Всегда обновляем позицию для плавной анимации движения
                // Используем линейную интерполяцию от initialZ к 0 (гриф)
                const newZ = note.initialZ + (0 - note.initialZ) * progress;
                
                // ВСЕГДА обновляем позицию каждый кадр для плавной анимации
                // Это критично для визуального отображения движения
                note.currentZ = newZ;
                note.mesh.position.z = newZ;

                // Маркер пальца создается для следующей ноты после того, как текущая достигла грифа

                // Обновляем масштаб для плавной анимации приближения
                // Ноты начинаются маленькими и увеличиваются по мере приближения к грифу
                const scale = 0.8 + 0.4 * (1 - progress);
                
                // Добавляем эффект пульсации при приближении к грифу
                if (progress > 0.7) {
                    const pulse = 1 + 0.1 * Math.sin(currentTime * 20);
                    note.mesh.scale.set(scale * pulse, scale * pulse, scale * pulse);
                } else {
                    note.mesh.scale.set(scale, scale, scale);
                }
                
                // Анимация эффекта slideTo
                if (note.slideEffect) {
                    this.animateSlideEffect(note, currentTime, progress);
                }
                
                // Анимация эффекта hammer
                if (note.hammerEffect) {
                    this.animateHammerEffect(note, currentTime, progress);
                }
                
                // Анимация эффекта bend
                if (note.bendEffect) {
                    this.animateBendEffect(note, currentTime, progress);
                }
                
                // НЕ меняем прозрачность во время движения - это вызывает моргание струн
                // Ноты создаются либо с opacity=1 (активные) либо opacity=0.3 (ожидающие)
                // и остаются с этим значением до достижения грифа
                
                // Убеждаемся, что ноты всегда видимы
                if (note.mesh) {
                    note.mesh.visible = true;
                    // Проверяем видимость всех детей
                    note.mesh.children.forEach(child => {
                        if (child) {
                            child.visible = true;
                        }
                    });
                }

                // Проверка достижения грифа
                if (progress >= 1 && !note.reachedFretboard) {
                    note.reachedFretboard = true;
                    note.displayStartTime = currentTime;
                    note.mesh.position.z = 0;
                    note.mesh.scale.set(1, 1, 1);
                    if (note.mesh.children[0] && note.mesh.children[0].material) {
                        note.mesh.children[0].material.opacity = 1;
                    }
                    
                    // Создаем маркеры пальцев для следующего аккорда сразу после того, как текущий достиг грифа
                    // Находим все ноты текущего аккорда (с одинаковым originalTargetTime)
                    const currentChordTime = note.originalTargetTime;
                    const currentChordNotes = this.movingNotes.filter(n => 
                        n.originalTargetTime === currentChordTime
                    );
                    
                    // Создаем маркеры для всех нот текущего аккорда, если их еще нет (для первого аккорда)
                    const currentChordNotesWithoutMarkers = currentChordNotes.filter(n => 
                        !n.fingerSphereCreated &&
                        n.noteData && 
                        n.noteData.finger !== undefined && 
                        n.noteData.finger !== null
                    );
                    
                    currentChordNotesWithoutMarkers.forEach(chordNote => {
                        const fingerCircle = this.createFingerCircle(
                            chordNote.noteData.finger, 
                            chordNote.startX, 
                            chordNote.startY,
                            chordNote.string
                        );
                        if (fingerCircle) {
                            fingerCircle.visible = true;
                            this.scene.add(fingerCircle);
                            chordNote.fingerSphere = fingerCircle;
                            chordNote.fingerSphereCreated = true;
                            this.fingerSpheres.push({
                                sphere: fingerCircle,
                                string: chordNote.string,
                                fret: chordNote.fret,
                                finger: chordNote.noteData.finger
                            });
                        }
                    });
                    
                    // Проверяем, все ли ноты текущего аккорда достигли грифа
                    const allChordNotesReached = currentChordNotes.every(n => n.reachedFretboard);
                    
                    if (allChordNotesReached) {
                        // Находим следующую группу нот (следующий аккорд) по originalTargetTime
                        const nextChordTimes = this.movingNotes
                            .filter(n => 
                                n.originalTargetTime > currentChordTime &&
                                !n.reachedFretboard
                            )
                            .map(n => n.originalTargetTime)
                            .filter((time, index, arr) => arr.indexOf(time) === index) // Уникальные значения
                            .sort((a, b) => a - b); // Сортируем по возрастанию
                        
                        if (nextChordTimes.length > 0) {
                            // Берем ближайший следующий аккорд
                            const nextChordTime = nextChordTimes[0];
                            const nextChordNotes = this.movingNotes.filter(n => 
                                n.originalTargetTime === nextChordTime &&
                                !n.reachedFretboard &&
                                !n.fingerSphereCreated &&
                                n.noteData && 
                                n.noteData.finger !== undefined && 
                                n.noteData.finger !== null
                            );
                            
                            // Создаем маркеры для всех нот следующего аккорда
                            nextChordNotes.forEach(nextNote => {
                                const fingerCircle = this.createFingerCircle(
                                    nextNote.noteData.finger, 
                                    nextNote.startX, 
                                    nextNote.startY,
                                    nextNote.string
                                );
                                if (fingerCircle) {
                                    fingerCircle.visible = true;
                                    this.scene.add(fingerCircle);
                                    nextNote.fingerSphere = fingerCircle;
                                    nextNote.fingerSphereCreated = true;
                                    this.fingerSpheres.push({
                                        sphere: fingerCircle,
                                        string: nextNote.string,
                                        fret: nextNote.fret,
                                        finger: nextNote.noteData.finger
                                    });
                                }
                            });
                        }
                    }
                    
                    needsHighlightUpdate = true;
                    needsTrackUpdate = true;
                }
            } else {
                // Нота на грифе - проверяем условия удаления и подсвечиваем ритмическое окно
                if (!note.displayStartTime) {
                    note.displayStartTime = currentTime;
                }

                // Создаем маркер пальца для нот на грифе (включая нулевой уровень)
                if (!note.fingerSphereCreated && 
                    note.noteData.finger !== undefined && 
                    note.noteData.finger !== null) {
                    const fingerCircle = this.createFingerCircle(
                        note.noteData.finger, 
                        note.startX, 
                        note.startY,
                        note.string
                    );
                    if (fingerCircle) {
                        fingerCircle.visible = true;
                        this.scene.add(fingerCircle);
                        note.fingerSphere = fingerCircle;
                        note.fingerSphereCreated = true;
                        this.fingerSpheres.push({
                            sphere: fingerCircle,
                            string: note.string,
                            fret: note.fret,
                            finger: note.noteData.finger
                        });
                    }
                }

                const timeOnFretboard = currentTime - note.displayStartTime;
                const isGaming = getGamingMode();
                const speedFactor = getSpeedFactor();

                // Скрываем маркер пальца после того, как нота отыграла (кроме нулевого уровня)
                // На нулевом уровне маркер остается видимым до тех пор, пока не исчезнет основная нота
                if (speedFactor !== 0 && timeOnFretboard >= note.durationSeconds && note.fingerSphere) {
                    note.fingerSphere.visible = false;
                }

                // Визуальная подсветка нот на грифе (без проверки временного окна)
                if (isGaming && speedFactor > 0 && !note.hit && !note.missed) {
                    if (note.mesh && note.mesh.children[0] && note.mesh.children[0].material) {
                        const mat = note.mesh.children[0].material;
                        // Все ноты на грифе подсвечиваем цветом струны
                        mat.opacity = 1;
                        const stringColor = this.getStringColor(note.string);
                        mat.color.setHex(stringColor); // Цвет струны вместо желтого
                        note.mesh.scale.set(1.35, 1.35, 1.35);
                        mat.needsUpdate = true;
                    }
                }
                
                // Анимация эффекта bend на грифе
                if (note.bendEffect) {
                    this.animateBendEffect(note, currentTime, 1);
                }

                const shouldRemoveByTime = this.shouldRemoveNoteByTime(note, timeOnFretboard);

                // Маркер пальца удаляется вместе с нотой (в removeMovingNotes)

                if (shouldRemoveByTime) {
                    if (isGaming && speedFactor !== 0) {
                        if (!note.hit && !note.missed) {
                            note.missed = true;
                            this.handleMissedNote(note);
                            needsHighlightUpdate = true;
                            needsTrackUpdate = true;
                            continue;
                        }
                    } else {
                        notesToRemove.push(i);
                    }
                }
            }
        }

        // Обновляем дорожки и подсветку только при необходимости или через интервал
        const timeSinceLastTrackUpdate = performance.now() - this.lastTrackUpdate;
        const timeSinceLastHighlightUpdate = performance.now() - this.lastHighlightUpdate;

        if (needsTrackUpdate || timeSinceLastTrackUpdate > this.trackUpdateInterval) {
            this.updateTrackPriorities();
            this.lastTrackUpdate = performance.now();
        }

        if (needsHighlightUpdate || timeSinceLastHighlightUpdate > this.highlightUpdateInterval) {
            this.updateFretHighlighting();
            this.lastHighlightUpdate = performance.now();
        }

        // Обновляем позицию камеры для следования за нотами (критично для мобильных)
        this.updateCameraToFollowNotes();

        if (notesToRemove.length > 0) {
            this.removeMovingNotes(notesToRemove);
        }
    }

    // Оптимизированная система приоритетов дорожек
    updateTrackPriorities() {
        // Собираем информацию о нотах близких к грифу (кэшируем фильтрацию)
        const nearbyNotes = this.movingNotes.filter(note =>
            note.currentZ <= 10 && note.currentZ >= -20 && !note.hit && !note.missed && !note.isWaiting
        );

        // Определяем, какие треки должны быть видны
        const tracksToShow = new Set();
        
        if (nearbyNotes.length > 0) {
            // Группируем по ладам и находим ближайшие ноты
            const fretDistances = {};
            nearbyNotes.forEach(note => {
                if (!fretDistances[note.fret] || note.currentZ > fretDistances[note.fret].currentZ) {
                    fretDistances[note.fret] = note;
                }
            });

            // Сортируем лады по близости к грифу и берем только приоритетные
            const sortedFrets = Object.values(fretDistances)
                .sort((a, b) => b.currentZ - a.currentZ)
                .slice(0, this.maxVisibleTracks);

            sortedFrets.forEach(note => {
                tracksToShow.add(note.fret);
            });

            // Фокусировка камеры теперь происходит в updateMovingNotes через updateCameraToFollowNotes
            // Оставляем эту логику как fallback для особых случаев
            // const fretsArray = sortedFrets.map(note => note.fret);
            // this.updateCameraFocus(fretsArray);
        } else {
            // this.updateCameraFocus([]);
        }

        // Батчим обновления видимости треков (обновляем только изменившиеся)
        for (let i = 0; i < this.fretTracks.length; i++) {
            const track = this.fretTracks[i];
            if (track) {
                const shouldBeVisible = tracksToShow.has(i) || this.fretNoteCounts[i] > 0;
                if (track.visible !== shouldBeVisible) {
                    track.visible = shouldBeVisible;
                }
            }
        }
    }

    // Проверка условий удаления ноты по времени
    shouldRemoveNoteByTime(note, timeOnFretboard) {
        // Игровой режим с нулевым уровнем: ноты не удаляются по времени
        if (getGamingMode() && getSpeedFactor() === 0) {
            return false;
        }

        // Если нота была на грифе дольше своей длительности - отмечаем как пропущенную
        if (timeOnFretboard >= note.durationSeconds) {
            // В игровом режиме: перемещаем в fallingNotes
            if (getGamingMode() && !note.hit && !note.missed) {
                note.missed = true;
                this.handleMissedNote(note);
                return false; // Не удаляем сразу - handleMissedNote обработает
            }
            return true; // В обычном режиме удаляем
        }

        return false;
    }

    // Улучшенный метод обработки пропущенных нот
    handleMissedNote(note) {
        if (!note) return;

        this.streak = 0;

        // Маркер пальца удалится вместе с нотой в removeMovingNotes

        // Находим индекс ноты в массиве
        const index = this.movingNotes.indexOf(note);
        if (index === -1) return;

        // Удаляем из movingNotes
        this.movingNotes.splice(index, 1);

        // Добавляем в fallingNotes для анимации падения
        note.falling = true;
        note.fallSpeed = 0.2;
        note.fallRotation = 0.1;
        this.fallingNotes.push(note);

        // Меняем цвет на красный
        note.mesh.children.forEach(child => {
            if (child.material) {
                child.material.color.setHex(0xFF0000);
            }
        });

        // Обновляем счетчики активных ладов
        this.removeActiveFret(note.fret);
    }

    // Улучшенный метод удаления нот по индексам
    removeMovingNotes(indicesToRemove) {
        if (!indicesToRemove || indicesToRemove.length === 0) {
            return;
        }

        // Сортируем индексы в обратном порядке для безопасного удаления
        const sortedIndices = [...indicesToRemove].sort((a, b) => b - a);

        const affectedFrets = new Set();

        sortedIndices.forEach(index => {
            if (index >= 0 && index < this.movingNotes.length) {
                const note = this.movingNotes[index];
                affectedFrets.add(note.fret);

                // Удаляем сферу пальца, если она есть
                if (note.fingerSphere) {
                    // Удаляем сферу из сцены, если она еще там
                    if (this.scene && note.fingerSphere.parent === this.scene) {
                        this.scene.remove(note.fingerSphere);
                    }
                    // Очищаем ресурсы сферы
                    note.fingerSphere.children.forEach(child => {
                        if (child.geometry) {
                            child.geometry.dispose();
                        }
                        if (child.material) {
                            if (child.material.map) child.material.map.dispose();
                            child.material.dispose();
                        }
                        // Очищаем детей (текст пальца)
                        if (child.children) {
                            child.children.forEach(grandChild => {
                                if (grandChild.geometry) {
                                    grandChild.geometry.dispose();
                                }
                                if (grandChild.material) {
                                    if (grandChild.material.map) grandChild.material.map.dispose();
                                    grandChild.material.dispose();
                                }
                            });
                        }
                    });
                    // Удаляем из массива fingerSpheres
                    const fingerIndex = this.fingerSpheres.findIndex(fs => 
                        fs.string === note.string && 
                        fs.fret === note.fret && 
                        fs.finger === note.noteData.finger
                    );
                    if (fingerIndex !== -1) {
                        this.fingerSpheres.splice(fingerIndex, 1);
                    }
                }

                // Удаляем ноту из сцены
                if (note.mesh && this.scene) {
                    this.scene.remove(note.mesh);
                    // Тщательно очищаем ресурсы (включая сферу пальца, если она была добавлена)
                    note.mesh.children.forEach(child => {
                        if (child.geometry) {
                            child.geometry.dispose();
                        }
                        if (child.material) {
                            if (child.material.map) child.material.map.dispose();
                            child.material.dispose();
                        }
                        // Если это группа сферы пальца, очищаем и её детей
                        if (child.children) {
                            child.children.forEach(grandChild => {
                                if (grandChild.geometry) {
                                    grandChild.geometry.dispose();
                                }
                                if (grandChild.material) {
                                    if (grandChild.material.map) grandChild.material.map.dispose();
                                    grandChild.material.dispose();
                                }
                            });
                        }
                    });
                }

                // Удаляем ноту из массива
                this.movingNotes.splice(index, 1);
            }
        });

        // Обновляем счетчики нот для затронутых ладов
        affectedFrets.forEach(fret => {
            const activeNotesCount = this.movingNotes.filter(note =>
                note.fret === fret && !note.hit && !note.missed
            ).length;

            this.fretNoteCounts[fret] = activeNotesCount;

            if (activeNotesCount === 0) {
                this.hideTrack(fret);
                this.updateFretHighlight(fret, false);
                this.activeFrets.delete(fret);
            }
        });

        // Обновляем фокусировку камеры
        // Камера теперь обновляется автоматически в updateMovingNotes через updateCameraToFollowNotes
        // if (this.activeFrets.size > 0) {
        //     this.updateCameraFocus(Array.from(this.activeFrets));
        // } else {
        //     this.resetCamera();
        // }

        if (this.isStaticGameMode()) {
            this.layoutStaticGameNotes();
        }
    }

    // Улучшенный метод удаления нот с грифа
    removeNoteFromFretboard(string, fret) {
        const notesToRemove = [];

        // Собираем индексы всех подходящих нот
        this.movingNotes.forEach((note, index) => {
            if (note.string === string &&
                note.fret === fret &&
                note.reachedFretboard) {
                notesToRemove.push(index);
            }
        });

        if (notesToRemove.length > 0) {
            // Удаляем в обратном порядке (метод removeMovingNotes уже обрабатывает сферы пальцев)
            this.removeMovingNotes(notesToRemove);
            return true;
        }
        return false;
    }

    markNoteAsHit(string, fret) {
        const currentTime = Tone.now();

        const isGaming = getGamingMode();
        const speedFactor = getSpeedFactor();

        // Ищем ВСЕ активные ноты по струне и ладу (для аккордов)
        const noteIndices = [];
        this.movingNotes.forEach((note, index) => {
            if (note.string === string &&
                note.fret === fret &&
                note.reachedFretboard &&
                !note.hit &&
                !note.missed) {

                // Убираем проверку временного окна - программа всегда слушает ноты
                noteIndices.push(index);
            }
        });

        if (noteIndices.length > 0) {
            noteIndices.forEach(noteIndex => {
                const note = this.movingNotes[noteIndex];
                note.hit = true;
                note.hitTime = currentTime;

                // Маркер пальца удалится вместе с нотой в removeMovingNotes

                // Анимация попадания - батчим изменения материала
                const originalColor = this.getStringColor(note.string);
                note.mesh.children.forEach(child => {
                    if (child.material) {
                        // Сохраняем оригинальный цвет для восстановления
                        if (!child.material.userData.originalColor) {
                            child.material.userData.originalColor = originalColor;
                        }
                        child.material.color.setHex(0x00FF00);
                        child.material.opacity = 1;
                        child.material.needsUpdate = true; // Помечаем как требующий обновления
                    }
                });

                // Анимация масштаба
                note.mesh.scale.set(1.5, 1.5, 1.5);

                // Возврат к нормальному размеру
                setTimeout(() => {
                    if (note.mesh) {
                        note.mesh.scale.set(1, 1, 1);
                        note.mesh.children.forEach(child => {
                            if (child.material && child.material.userData.originalColor !== undefined) {
                                child.material.color.setHex(child.material.userData.originalColor);
                                child.material.opacity = 0.8;
                                child.material.needsUpdate = true;
                            }
                        });
                    }
                }, 200);

                this.hitNotes++;
                this.streak++;

                // После короткой анимации попадания аккуратно убираем ноту с грифа,
                // чтобы она не "зависала" и не мешала следующим
                setTimeout(() => {
                    const idx = this.movingNotes.indexOf(note);
                    if (idx !== -1) {
                        this.removeMovingNotes([idx]);
                    }
                }, 250);
            });

            // Обновляем подсветку ладов (помечаем как требующее обновления)
            this.lastHighlightUpdate = 0; // Принудительно обновляем при изменении
            this.updateFretHighlighting();

            return true;
        }

        return false;
    }

    cleanupStuckNotes() {
        // В статическом игровом режиме (скорость 0) ноты должны оставаться на грифе,
        // поэтому полностью отключаем авто-очистку зависших нот
        if (this.isStaticGameMode()) {
            return;
        }

        const currentTime = Tone.now();
        const stuckNotesIndices = [];

        // Ищем ноты, которые слишком долго находятся на грифе
        this.movingNotes.forEach((note, index) => {
            if (note.reachedFretboard && note.displayStartTime) {
                const timeOnFretboard = currentTime - note.displayStartTime;
                // Увеличиваем лимит времени для зависших нот
                if (timeOnFretboard > 15) { // 15 секунд вместо 10
                    stuckNotesIndices.push(index);
                }
            }
        });

        if (stuckNotesIndices.length > 0) {
            this.removeMovingNotes(stuckNotesIndices);
        }
    }

    // Обновленный метод для полной очистки всех нот
    clearAllMovingNotes() {

        // Собираем все ноты для удаления
        const notesToRemove = [...this.movingNotes];

        // Очищаем массивы ДО удаления из сцены
        this.movingNotes = [];
        this.fallingNotes = [];
        this.appearingNotes = [];
        this.noteSequenceCounter = 0;

        // Удаляем каждую ноту из сцены
        notesToRemove.forEach(note => {
            if (note.mesh && this.scene) {
                this.scene.remove(note.mesh);
                // Очищаем геометрию и материалы
                note.mesh.children.forEach(child => {
                    if (child.geometry) {
                        child.geometry.dispose();
                    }
                    if (child.material) {
                        if (child.material.map) child.material.map.dispose();
                        child.material.dispose();
                    }
                });
            }
        });

        // Удаляем все сферы пальцев
        this.fingerSpheres.forEach(fingerSphere => {
            if (fingerSphere.sphere && this.scene) {
                this.scene.remove(fingerSphere.sphere);
                // Очищаем геометрию и материалы
                fingerSphere.sphere.children.forEach(child => {
                    if (child.geometry) {
                        child.geometry.dispose();
                    }
                    if (child.material) {
                        if (child.material.map) child.material.map.dispose();
                        child.material.dispose();
                    }
                });
            }
        });
        this.fingerSpheres = [];

        // Сбрасываем состояния
        this.activeFrets.clear();
        this.fretNoteCounts.fill(0);
        this.highlightedFret = null;

        // Скрываем все подсветки и треки
        this.fretHighlights.forEach(highlight => {
            if (highlight) highlight.visible = false;
        });
        this.hideAllTracks();

    }

    // Полный сброс состояния - останавливает все анимации и сбрасывает камеру
    fullReset() {
        // Останавливаем анимацию камеры
        if (this.cameraAnimation) {
            cancelAnimationFrame(this.cameraAnimation);
            this.cameraAnimation = null;
        }

        // Очищаем все ноты
        this.clearAllMovingNotes();

        // Сбрасываем камеру в исходное положение
        if (this.camera) {
            this.camera.position.x = 0;
            this.camera.position.z = 25;
            this.camera.lookAt(0, -2, 0);
            this.lastCameraPosition = { x: 0, z: 25 };
        }

        // Сбрасываем счетчики
        this.streak = 0;
        this.totalNotes = 0;
        this.hitNotes = 0;
    }

    // Метод для очистки нот после воспроизведения (удаляет только ноты, которые уже отыграли)
    cleanupNotesAfterPlayback() {
        const currentTime = Tone.now();
        const notesToRemove = [];

        // Ищем ноты, которые уже отыграли свое время звучания
        for (let i = this.movingNotes.length - 1; i >= 0; i--) {
            const note = this.movingNotes[i];
            if (note.reachedFretboard && note.displayStartTime) {
                const timeOnFretboard = currentTime - note.displayStartTime;
                // Адаптивная толерантность для удаления нот: на высоких скоростях увеличиваем окно
                const isGaming = getGamingMode();
                const speedFactor = getSpeedFactor ? getSpeedFactor() : 1;
                const baseTolerance = 0.1;
                const maxTolerance = 0.25;
                const adaptiveTolerance = isGaming && speedFactor > 0 
                    ? baseTolerance + (maxTolerance - baseTolerance) * speedFactor 
                    : baseTolerance;
                // Если нота была на грифе дольше своей длительности + адаптивная задержка
                if (timeOnFretboard >= note.durationSeconds + adaptiveTolerance) {
                    notesToRemove.push(i);
                }
            }
        }

        if (notesToRemove.length > 0) {
            this.removeMovingNotes(notesToRemove);
        }
    }

    // Обновляем метод анимации для периодической очистки
    // Оптимизированный метод анимации
    // Мониторинг и адаптация производительности
    checkPerformance(currentTime) {
        // Вычисляем текущий FPS
        const deltaTime = currentTime - this.lastFrameTime;
        if (deltaTime > 0) {
            const currentFPS = 1000 / deltaTime;
            this.fpsHistory.push(currentFPS);
            
            // Сохраняем только последние 30 значений
            if (this.fpsHistory.length > 30) {
                this.fpsHistory.shift();
            }
        }
        
        // Проверяем FPS каждые 2 секунды
        if (currentTime - this.lastFPSCheck > this.fpsCheckInterval && this.fpsHistory.length > 10) {
            const avgFPS = this.fpsHistory.reduce((a, b) => a + b, 0) / this.fpsHistory.length;
            this.fps = avgFPS;
            
            // Адаптируем качество на основе FPS
            if (this.performanceMode === 'auto') {
                if (avgFPS < 30 && this.qualityLevel !== 'low') {
                    this.lowFPSCounter++;
                    if (this.lowFPSCounter >= 2) {
                        this.setQualityLevel('low');
                        console.log('Переключение на низкое качество, FPS:', avgFPS.toFixed(1));
                    }
                } else if (avgFPS < 45 && this.qualityLevel === 'high') {
                    this.lowFPSCounter++;
                    if (this.lowFPSCounter >= 2) {
                        this.setQualityLevel('medium');
                        console.log('Переключение на среднее качество, FPS:', avgFPS.toFixed(1));
                    }
                } else if (avgFPS > 55 && this.qualityLevel !== 'high') {
                    this.lowFPSCounter = 0;
                    // Не повышаем автоматически, чтобы не создавать скачков
                } else {
                    this.lowFPSCounter = 0;
                }
            }
            
            this.lastFPSCheck = currentTime;
        }
        
        this.lastFrameTime = currentTime;
    }
    
    // Установка уровня качества
    setQualityLevel(level) {
        if (this.qualityLevel === level) return;
        
        this.qualityLevel = level;
        
        if (!this.renderer) return;
        
        switch(level) {
            case 'low':
                // Минимальные настройки для старых ПК
                this.renderer.setPixelRatio(1);
                this.renderer.shadowMap.enabled = false;
                this.highlightUpdateInterval = 150;
                this.trackUpdateInterval = 100;
                break;
                
            case 'medium':
                // Средние настройки
                this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 1.5));
                this.renderer.shadowMap.enabled = false;
                this.highlightUpdateInterval = 100;
                this.trackUpdateInterval = 50;
                break;
                
            case 'high':
                // Максимальные настройки
                this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
                this.renderer.shadowMap.enabled = false; // Тени всё равно не используются для нот
                this.highlightUpdateInterval = 100;
                this.trackUpdateInterval = 50;
                break;
        }
    }

    animate() {
        if (!this.renderer || !this.scene || !this.camera) {
            this.animationId = requestAnimationFrame(() => this.animate());
            return;
        }

        this.animationId = requestAnimationFrame(() => this.animate());

        const currentTime = performance.now();
        this.frameCount++;
        
        // Проверяем производительность
        this.checkPerformance(currentTime);

        // Периодическая проверка на зависшие ноты (каждые 5 секунд)
        if (!this.lastCleanup || Tone.now() - this.lastCleanup > 5) {
            this.cleanupStuckNotes();
            this.lastCleanup = Tone.now();
        }

        // Обновляем ноты каждый кадр (это критично для плавности)
        this.updateMovingNotes();

        // Обновляем появляющиеся и падающие ноты
        this.updateAppearingNotes();
        this.updateFallingNotes();

        // Рендерим сцену
        this.renderer.render(this.scene, this.camera);
    }

    // В методе displayNote убедиться, что в игровом режиме используются движущиеся ноты
    displayNote(noteData, nextNoteData = null, audioStartTime = null) {
        if (!this.scene) {
            return;
        }

        // ВСЕГДА используем движущиеся ноты в обоих режимах
        const notes = Array.isArray(noteData) ? noteData : [noteData];
        const currentTime = Tone.now();

        // Очищаем только если это новая последовательность
        if (getGamingMode()) {
            //this.clearAllMovingNotes();
        }

        // Собираем все ноты для фокусировки камеры
        const blockNotes = notes.filter(note => note && note.string !== undefined && note.fret !== undefined);
        if (blockNotes.length > 0) {
            this.animateCameraToBlock(blockNotes);
        }

        // Создаем движущиеся ноты для каждой ноты
        notes.forEach((note, index) => {
            if (!note || note.string === undefined || note.fret === undefined) return;

            // Ищем следующую ноту на той же струне для эффекта hammer
            let nextNoteOnString = null;
            if (note.effect && note.effect.hammer) {
                // Сначала проверяем nextNoteData параметр
                if (nextNoteData && nextNoteData.string === note.string) {
                    nextNoteOnString = nextNoteData;
                } else {
                    // Ищем в текущем массиве нот
                    for (let i = index + 1; i < notes.length; i++) {
                        if (notes[i] && notes[i].string === note.string) {
                            nextNoteOnString = notes[i];
                            break;
                        }
                    }
                }
            }

            const targetTime = audioStartTime || (currentTime + 4.0);
            this.addMovingNote(note, currentTime, targetTime, true, nextNoteOnString);
        });
    }

    // Остальные методы остаются без изменений
    showFretTrack(fret) {
        if (this.fretTracks[fret]) {
            this.fretTracks[fret].visible = true;
        }
    }

    hideAllFretTracks() {
        this.fretTracks.forEach(track => {
            if (track) track.visible = false;
        });
    }

    clearAllNotes() {
        this.notes.forEach(note => {
            if (note.mesh && this.scene) {
                this.scene.remove(note.mesh);
                note.mesh.children.forEach(child => {
                    if (child.geometry) child.geometry.dispose();
                    if (child.material) {
                        if (child.material.map) child.material.map.dispose();
                        child.material.dispose();
                    }
                });
            }
        });
        this.notes = [];
        this.appearingNotes = [];
        this.clearNextNotes();
        this.clearFallingNotes();
        this.hideAllFretTracks();
    }

    clearFallingNotes() {
        this.fallingNotes.forEach(note => {
            if (note.mesh && this.scene) {
                this.scene.remove(note.mesh);
                this.safeDisposeMesh(note.mesh);
            }
        });
        this.fallingNotes = [];
    }

    clearNextNotes() {
        this.nextNotes.forEach(noteGroup => {
            this.scene.remove(noteGroup);
            this.safeDisposeMesh(noteGroup);
        });
        this.nextNotes = [];
    }

    createNoteText(noteName, opacity = 1) {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = 96;
        canvas.height = 96;

        context.clearRect(0, 0, canvas.width, canvas.height);
        context.font = 'Bold 50px Arial';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillStyle = `rgba(255, 255, 255, ${opacity})`;
        context.fillText(noteName, 48, 48);

        const texture = new THREE.CanvasTexture(canvas);
        const material = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            opacity: opacity
        });

        const sprite = new THREE.Sprite(material);
        sprite.scale.set(0.8, 0.8, 1);

        return sprite;
    }

    calculateFretPositions() {
        const positions = [0];

        for (let i = 0; i < this.fretCount; i++) {
            const position = (this.fretWidth * 1.33) * (1 - Math.pow(2, -i / 12));
            positions.push(position);
        }

        return positions;
    }

    calculateFretStringPositions() {
        const positions = [];
        const stringAreaHeight = this.fretHeight - 0.5;

        for (let fret = 0; fret < this.fretCount; fret++) {
            const stringPositions = {};

            const currentFretX = -this.fretWidth/2 + this.fretPositions[fret];
            const nextFretX = -this.fretWidth/2 + this.fretPositions[fret + 1];
            const middleX = (currentFretX + nextFretX) / 2;

            for (let string = 0; string < 6; string++) {
                const yPos = -stringAreaHeight/2 + (string / 5) * stringAreaHeight;
                stringPositions[string] = { x: middleX, y: yPos };
            }

            positions[fret] = stringPositions;
        }

        const lastFretPositions = {};
        const lastFretX = -this.fretWidth/2 + this.fretPositions[this.fretCount];
        for (let string = 0; string < 6; string++) {
            const yPos = -stringAreaHeight/2 + (string / 5) * stringAreaHeight;
            lastFretPositions[string] = { x: lastFretX, y: yPos };
        }
        positions[this.fretCount] = lastFretPositions;

        return positions;
    }

    init() {
        this.scene = new THREE.Scene();
        this.scene.background = new THREE.Color(0x001122);

        this.container = document.getElementById('threejs-container');
        if (!this.container) {
            console.error('Контейнер threejs-container не найден');
            return;
        }

        const containerWidth = this.container.clientWidth || 1500;
        const containerHeight = this.container.clientHeight || 600;

        this.camera = new THREE.PerspectiveCamera(45, containerWidth / containerHeight, 0.1, 1000);
        this.camera.position.set(0, 5, 25);
        this.camera.lookAt(0, -2, 0);

        // Сохраняем оригинальную позицию камеры
        this.originalCameraPosition = this.camera.position.clone();

        // Детекция слабого железа перед созданием рендерера
        const isLowEndDevice = this.detectLowEndDevice();
        if (isLowEndDevice) {
            console.log('Обнаружен слабый ПК, оптимизации включены');
            this.qualityLevel = 'medium';
        }
        
        // Инициализируем пулы геометрий с адаптивным качеством
        this.initGeometryPools(isLowEndDevice);

        // Проверяем поддержку WebGL перед созданием renderer
        try {
            const canvas = document.createElement('canvas');
            const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
            if (!gl) {
                throw new Error('WebGL не поддерживается в этом браузере');
            }
        } catch (error) {
            console.error('WebGL не поддерживается:', error);
            this.showWebGLError();
            return;
        }

        try {
            this.renderer = new THREE.WebGLRenderer({
                antialias: !isLowEndDevice, // Отключаем на слабых ПК
                alpha: true,
                powerPreference: "high-performance",
                preserveDrawingBuffer: false, // Оптимизация памяти
                stencil: false, // Отключаем stencil буфер если не используется
                depth: true,
                logarithmicDepthBuffer: true, // Лучшая точность глубины - устраняет z-fighting
                failIfMajorPerformanceCaveat: false // Не прерывать инициализацию при проблемах с производительностью
            });
        } catch (error) {
            console.error('Ошибка создания WebGL контекста:', error);
            this.showWebGLError(error.message);
            return;
        }

        this.renderer.setSize(containerWidth, containerHeight);
        this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2)); // Ограничиваем pixel ratio для производительности
        this.renderer.shadowMap.enabled = false; // Тени отключены по умолчанию для производительности
        
        // Оптимизация рендеринга
        this.renderer.sortObjects = true; // Сортировка нужна для правильной отрисовки прозрачных объектов
        this.renderer.physicallyCorrectLights = false; // Отключаем физически корректное освещение для производительности

        this.container.innerHTML = '';
        this.container.appendChild(this.renderer.domElement);

        this.setupLighting();
        this.createFretboardXY();
        this.addFretLabels();
        this.initNoteTracks();
        this.createFretHighlights(); // Добавляем подсветку ладов

        window.addEventListener('resize', () => this.onWindowResize());
        
        // Обработчик изменения ориентации на мобильных устройствах
        if (window.screen && window.screen.orientation) {
            window.screen.orientation.addEventListener('change', () => {
                this.isMobileDevice = this.detectMobileDevice();
                this.onWindowResize();
            });
        } else {
            // Fallback для старых браузеров
            window.addEventListener('orientationchange', () => {
                this.isMobileDevice = this.detectMobileDevice();
                setTimeout(() => this.onWindowResize(), 100);
            });
        }

        this.animate();

        setTimeout(() => {
            this.onWindowResize();
            setTimeout(() => this.onWindowResize(), 500);
        }, 300);
    }

    setupLighting() {
        // Ambient свет для общего освещения
        const ambientLight = new THREE.AmbientLight(0x4466ff, 0.6);
        this.scene.add(ambientLight);

        // Один DirectionalLight достаточно (объединяем оба предыдущих)
        const directionalLight = new THREE.DirectionalLight(0xffffff, 0.9);
        directionalLight.position.set(3, 8, 8);
        // Тени отключены для производительности (включаются через setQualityLevel если нужно)
        directionalLight.castShadow = false;
        this.scene.add(directionalLight);
    }

    createFretboardXY() {
        // Если группа уже существует, не пересоздаём
        if (this.fretboardGroup) {
            console.warn('Fretboard group already exists, skipping recreation');
            return;
        }
        
        this.fretboardGroup = new THREE.Group();
        this.fretboardGroup.frustumCulled = false; // Всегда отрисовываем гриф

        this.addStrings(this.fretboardGroup);
        this.addFrets(this.fretboardGroup);
        this.addFretMarkers(this.fretboardGroup);

        this.fretboardGroup.position.y = -8;
        this.scene.add(this.fretboardGroup);
        console.log('Fretboard created with', this.fretboardGroup.children.length, 'children');

        this.addFretTracks();
    }

    addFretLabels() {
        const fretLabelGroup = new THREE.Group();

        for (let fret = 1; fret <= this.fretCount; fret++) {
            const label = this.createTextLabel(fret.toString(), 0.7);
            const delta = this.fretPositions[fret + 1] - this.fretPositions[fret];
            let xPos = -this.fretWidth/2 + (this.fretPositions[fret] + (delta / 2));
            label.position.set(xPos, -this.fretHeight * 2, 0.1);
            fretLabelGroup.add(label);
        }

        this.scene.add(fretLabelGroup);
    }

    createTextLabel(text, opacity = 1) {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = 128;
        canvas.height = 50;

        context.clearRect(0, 0, canvas.width, canvas.height);
        context.font = 'Bold 70px Arial';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        context.fillStyle = `rgba(255, 255, 255, ${opacity})`;
        context.fillText(text, 64, 32);

        const texture = new THREE.CanvasTexture(canvas);
        const material = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            opacity: opacity
        });

        const sprite = new THREE.Sprite(material);
        sprite.scale.set(1.2, 0.6, 1);

        return sprite;
    }

    // Создание окружности пальца на грифе (вместо сферы)
    createFingerCircle(fingerNumber, x, y, string) {
        if (!this.scene) return null;

        // Получаем цвет струны
        const stringColor = this.getStringColor(string);

        // Создаем окружность (кольцо) с цветом струны (увеличенного размера)
        // Используем геометрию из пула
        const ringGeometry = this.geometryPool.ring;
        
        // НЕ переиспользуем материал - opacity может меняться при анимации
        const ringMaterial = new THREE.MeshBasicMaterial({
            color: stringColor,
            transparent: true,
            opacity: 0.7,
            side: THREE.DoubleSide
        });

        const ring = new THREE.Mesh(ringGeometry, ringMaterial);
        // Окружность в плоскости XY (без поворота)
        ring.position.set(0, 0, 0); // Позиция относительно группы

        // Создаем текст с номером пальца (увеличенного размера)
        const fingerText = this.createFingerText(fingerNumber.toString());
        fingerText.scale.set(1.3, 1.3, 1); // Увеличиваем размер текста
        fingerText.position.set(0, 0, 0.35); // В центре окружности, ближе к камере на 0.1

        const fingerGroup = new THREE.Group();
        fingerGroup.add(ring);
        fingerGroup.add(fingerText);
        fingerGroup.position.set(x, y, 0.3); // Позиция группы перед грифом на z=0.3

        return fingerGroup;
    }

    // Создание текста для номера пальца
    createFingerText(text) {
        const canvas = document.createElement('canvas');
        const context = canvas.getContext('2d');
        canvas.width = 128;
        canvas.height = 128;

        context.clearRect(0, 0, canvas.width, canvas.height);
        // Увеличиваем размер шрифта для лучшей видимости
        context.font = 'Bold 80px Arial';
        context.textAlign = 'center';
        context.textBaseline = 'middle';
        // Белый текст с черной обводкой для лучшей видимости
        context.strokeStyle = 'rgba(0, 0, 0, 1)';
        context.lineWidth = 4;
        context.strokeText(text, 64, 64);
        context.fillStyle = 'rgba(255, 255, 255, 1)'; // Белый текст
        context.fillText(text, 64, 64);

        const texture = new THREE.CanvasTexture(canvas);
        texture.needsUpdate = true;
        const material = new THREE.SpriteMaterial({
            map: texture,
            transparent: true,
            opacity: 1
        });

        const sprite = new THREE.Sprite(material);
        sprite.scale.set(1.0, 1.0, 1); // Увеличиваем размер спрайта

        return sprite;
    }

    addStrings(group) {
        const stringThickness = [0.015, 0.02, 0.025, 0.03, 0.035, 0.04]; // Увеличены для лучшей видимости
        const stringNotes = ['E', 'B', 'G', 'D', 'A', 'E'];
        
        // Создаём геометрии струн (вызывается один раз)
        if (!this.geometryPool.strings) {
            this.geometryPool.strings = stringThickness.map(thickness => 
                new THREE.BoxGeometry(this.fretWidth, thickness, 0.02) // Увеличена глубина
            );
        }
        
        for (let i = 0; i < 6; i++) {
            const stringGeometry = this.geometryPool.strings[i];
            
            // НЕ переиспользуем материал для статичных струн - каждая струна имеет свой материал
            const stringMaterial = new THREE.MeshBasicMaterial({
                color: this.getStringColor(i+1),
                // КРИТИЧНО: непрозрачный материал без альфа-блендинга
                transparent: false,
                depthWrite: true,
                depthTest: true
            });

            const string = new THREE.Mesh(stringGeometry, stringMaterial);
            const stringAreaHeight = this.fretHeight - 0.5;
            const yPos = -stringAreaHeight/2 + (i / 5) * stringAreaHeight;
            // Z=0.1 чтобы струны были ближе к камере, чем ноты на z=0
            string.position.set(0, yPos, 0.1);
            string.frustumCulled = false; // Не скрывать при выходе из frustum
            group.add(string);

            const stringNumber = this.createTextLabel((i+1).toString(), 0.7);
            stringNumber.position.set(-this.fretWidth/2 - 1.2, yPos , 0.1);
            group.add(stringNumber);

            const noteName = this.createTextLabel(stringNotes[i], 0.7);
            noteName.position.set(-this.fretWidth/2 - 0.6, yPos, 0.1);
            group.add(noteName);
        }
    }

    addFrets(group) {
        // Создаём геометрию лада один раз
        if (!this.geometryPool.fret) {
            this.geometryPool.fret = new THREE.BoxGeometry(0.08, this.fretHeight, 0.05);
        }
        
        const fretGeometry = this.geometryPool.fret;
        
        // НЕ переиспользуем материал - каждый лад имеет свой материал
        for (let i = 0; i <= this.fretCount; i++) {
            const fretMaterial = new THREE.MeshBasicMaterial({
                color: 0xcccccc,
                transparent: false,
                opacity: 1
            });
            
            const fret = new THREE.Mesh(fretGeometry, fretMaterial);
            const xPos = -this.fretWidth/2 + this.fretPositions[i];
            fret.position.set(xPos, 0, 0.02);
            fret.renderOrder = 0; // Обычный порядок отрисовки
            group.add(fret);
        }
    }

    addFretMarkers(group) {
        const markerFrets = [3, 5, 7, 9, 12, 15, 17, 19, 21];

        markerFrets.forEach(fret => {
            if (fret <= this.fretCount) {
                const markerGeometry = new THREE.CircleGeometry(0.3, 8);
                const markerMaterial = new THREE.MeshBasicMaterial({
                    color: 0xffffff,
                    transparent: false,
                    opacity: 1
                });

                const delta = this.fretPositions[fret + 1] - this.fretPositions[fret];
                const xPos = -this.fretWidth/2 + (this.fretPositions[fret] + (delta / 2));

                if (fret === 12) {
                    const marker1 = new THREE.Mesh(markerGeometry, markerMaterial);
                    marker1.position.set(xPos, this.fretHeight/4, 0.3);
                    group.add(marker1);

                    const marker2 = new THREE.Mesh(markerGeometry, markerMaterial);
                    marker2.position.set(xPos, -this.fretHeight/4, 0.3);
                    group.add(marker2);
                } else {
                    const marker = new THREE.Mesh(markerGeometry, markerMaterial);
                    marker.position.set(xPos, 0, 0.3);
                    group.add(marker);
                }
            }
        });
    }

    getStringColor(string) {
        const colors = [0xff4444, 0xffaa00, 0xffff00, 0x00ff00, 0x4444ff, 0xaa00ff];
        return colors[string-1] || 0xffffff;
    }

    updateFallingNotes() {
        const notesToRemove = [];

        this.fallingNotes.forEach((note, index) => {
            // Ускоренное падение и затухание для быстрого удаления
            note.mesh.position.y -= 0.5; // Было 0.2, теперь быстрее
            note.mesh.position.x += (Math.random() - 0.5) * 0.05;
            note.mesh.rotation.z += 0.15; // Быстрее вращение
            note.mesh.scale.multiplyScalar(0.92); // Быстрее уменьшение

            note.mesh.children.forEach(child => {
                if (child.material) {
                    child.material.opacity *= 0.85; // Быстрее затухание (было 0.95)
                }
            });

            // Удаляем раньше: при y < -10 (было -15) или opacity < 0.1 (было 0.05)
            if (note.mesh.position.y < -10 ||
                (note.mesh.children[0] && note.mesh.children[0].material.opacity < 0.1)) {
                notesToRemove.push(index);
            }
        });

        this.removeFallingNotes(notesToRemove);
    }

    updateAppearingNotes() {
        const appearingNotesToRemove = [];

        this.appearingNotes.forEach((note, index) => {
            note.appearanceProgress += 0.1;

            if (note.appearanceProgress >= 1) {
                note.mesh.position.y = note.targetY;
                note.appearing = false;
                appearingNotesToRemove.push(index);
            } else {
                const jumpHeight = 0.3;
                const progress = note.appearanceProgress;
                const yOffset = jumpHeight * Math.sin(progress * Math.PI);
                note.mesh.position.y = note.targetY + yOffset;

                const scale = 0.8 + 0.2 * progress;
                note.mesh.scale.set(scale, scale, scale);
            }
        });

        appearingNotesToRemove.sort((a, b) => b - a).forEach(index => {
            this.appearingNotes.splice(index, 1);
        });
    }

    removeFallingNotes(indicesToRemove) {
        indicesToRemove.sort((a, b) => b - a).forEach(index => {
            if (index >= 0 && index < this.fallingNotes.length) {
                const note = this.fallingNotes[index];
                if (note.mesh && this.scene) {
                    this.scene.remove(note.mesh);
                    note.mesh.children.forEach(child => {
                        if (child.geometry) child.geometry.dispose();
                        if (child.material) {
                            if (child.material.map) child.material.map.dispose();
                            child.material.dispose();
                        }
                    });
                }
                this.fallingNotes.splice(index, 1);
            }
        });
    }

    reset() {
        this.clearAllNotes();
        this.clearAllMovingNotes();
        this.streak = 0;
        this.totalNotes = 0;
        this.hitNotes = 0;
    }

    showWebGLError(errorMessage = '') {
        if (!this.container) return;
        
        const errorText = errorMessage || 'WebGL не поддерживается в вашем браузере';
        this.container.innerHTML = `
            <div style="
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                height: 100%;
                padding: 20px;
                text-align: center;
                color: #fff;
                background: rgba(10, 15, 35, 0.9);
            ">
                <div style="font-size: 24px; margin-bottom: 20px; color: #ff5252;">
                    ⚠ Ошибка инициализации 3D визуализации
                </div>
                <div style="font-size: 16px; margin-bottom: 10px;">
                    ${errorText}
                </div>
                <div style="font-size: 14px; color: #aaa; margin-top: 20px;">
                    Попробуйте обновить браузер или использовать другой браузер (Chrome, Firefox, Edge)
                </div>
            </div>
        `;
    }

    onWindowResize() {
        if (!this.container || !this.camera || !this.renderer) return;

        const width = this.container.clientWidth || 1500;
        const height = this.container.clientHeight || 600;

        // Обновляем флаг мобильного устройства при изменении размера
        this.isMobileDevice = this.detectMobileDevice();

        this.camera.aspect = width / height;
        this.camera.updateProjectionMatrix();
        this.renderer.setSize(width, height);

        // Принудительно обновляем позицию камеры для правильного отображения на новом размере
        if (this.movingNotes.length > 0) {
            this.updateCameraToFollowNotes();
        }

        if (this.scene && this.camera) {
            this.renderer.render(this.scene, this.camera);
        }
    }

    // Обновленный метод создания треков с прозрачностью 10%
    addFretTracks() {
        const trackGroup = new THREE.Group();

        for (let fret = 0; fret < this.fretCount; fret++) {
            const currentFretX = -this.fretWidth/2 + this.fretPositions[fret];
            const nextFretX = -this.fretWidth/2 + this.fretPositions[fret + 1];
            const trackWidth = nextFretX - currentFretX;

            const trackGeometry = new THREE.PlaneGeometry(trackWidth, 200); // Увеличиваем длину треков
            const trackMaterial = new THREE.MeshBasicMaterial({
                color: this.getTrackColor(fret),
                transparent: true,
                opacity: 0.1, // 10% прозрачности
                side: THREE.DoubleSide
            });

            const track = new THREE.Mesh(trackGeometry, trackMaterial);
            const xPosition = currentFretX + trackWidth / 2;
            const yPosition = -8 - this.fretHeight / 2;
            const zPosition = -100; // Начинаем дальше назад

            track.position.set(xPosition, yPosition, zPosition);
            track.rotation.x = Math.PI / 2;
            track.visible = false;

            trackGroup.add(track);
            this.fretTracks[fret] = track;
        }

        this.scene.add(trackGroup);
    }

    displayAllBlockNotes(blockMeasures, tempo = 120) {
        if (!this.scene) return;

        this.clearAllMovingNotes();

        // Собираем плоский список всех нот для поиска следующей ноты для hammer
        const flatNotes = [];
        blockMeasures.forEach(measure => {
            measure.forEach(chordGroup => {
                const isPause = chordGroup[0] && chordGroup[0].pause === "true";
                if (!isPause) {
                    chordGroup.forEach(noteData => {
                        flatNotes.push(noteData);
                    });
                }
            });
        });

        const currentTime = Tone.now();
        let cumulativeTime = 0;
        let noteIndex = 0;

        // Проходим по всем мерам и аккордам блока
        blockMeasures.forEach(measure => {
            measure.forEach(chordGroup => {
                const isPause = chordGroup[0] && chordGroup[0].pause === "true";

                if (!isPause) {
                    // Рассчитываем время начала для этой группы нот
                    const groupStartTime = currentTime + cumulativeTime;

                    // Создаем движущиеся ноты для всех нот в аккорде
                    chordGroup.forEach(noteData => {
                        // Ищем следующую ноту на той же струне для эффекта hammer
                        let nextNoteOnString = null;
                        if (noteData.effect && noteData.effect.hammer) {
                            for (let i = noteIndex + 1; i < flatNotes.length; i++) {
                                if (flatNotes[i].string === noteData.string) {
                                    nextNoteOnString = flatNotes[i];
                                    break;
                                }
                            }
                        }
                        
                        this.addMovingNote(
                            noteData,
                            currentTime,
                            groupStartTime,
                            true,
                            nextNoteOnString
                        );
                        noteIndex++;
                    });

                    // Увеличиваем cumulativeTime на длительность этой группы
                    const durationValue = chordGroup[0].duration;
                    const isDotted = chordGroup[0].dotted;
                    const stepDuration = 4 / durationValue;
                    const actualDuration = isDotted ? stepDuration * 1.5 : stepDuration;
                    cumulativeTime += actualDuration * (60 / tempo);
                } else {
                    // Обрабатываем паузу
                    const pauseDuration = chordGroup[0].duration;
                    const isDotted = chordGroup[0].dotted || false;
                    const stepDuration = 4 / pauseDuration;
                    const actualDuration = isDotted ? stepDuration * 1.5 : stepDuration;
                    cumulativeTime += actualDuration * (60 / tempo);
                }
            });
        });
    }
}

/** Глобальный экземпляр визуализатора грифа и нот */
export const threeJSBand = new ThreeJSBand();